/**
 * Project detail screen — three-pane shell.
 *
 * Left  (collapsible + resizable): Captures list sidebar
 * Center (flex:1):                 Selected capture media or 3D viewer
 * Right  (collapsible + resizable): Overview / Estimate / Compliance tabs
 */
import { api } from "../api/client.ts";
import type {
  CaptureJobStatus,
  HouseComposition,
  HouseFloor,
  HouseFloorplanEntry,
  HousePanoramaEntry,
  HouseRoomEntry,
  IcLocal3DStatus,
  IcProject,
  IcScanFile,
  Manifest,
  ManifestCapture,
  ManifestInspection,
  ManifestRoom,
  StreetViewManifest,
} from "../types.ts";
import { OverviewTab } from "./ic_tabs/OverviewTab.ts";
import { EstimateTab } from "./ic_tabs/EstimateTab.ts";
import { ComplianceTab } from "./ic_tabs/ComplianceTab.ts";
import { HouseTab } from "./ic_tabs/HouseTab.ts";
import type { AnchoredPhoto } from "./ic_tabs/HouseTab.ts";
import { HouseComposerView } from "../viewer/HouseComposerView.ts";
import { CapturePanoViewer } from "../viewer/CapturePanoViewer.ts";
import { PHASE_COLORS, MEDIA_ICON } from "../constants.ts";
import { renderRoomPlanJson2D } from "./RoomPlanJson2D.ts";
import type { IcRoomPlanJson } from "./RoomPlanJson2D.ts";
import type { DxfViewerElement } from "dxf-lit";

type TabKey = "overview" | "estimate" | "compliance";
type CenterMode = "placeholder" | "capture" | "3d" | "client-preview";
type LeftMode = "captures" | "house";
type DateRange = "all" | "today" | "7d" | "30d";

type CaptureFilter = {
  phase: string;
  media: string;
  room: string;
  inspection: string;
  dateRange: DateRange;
};

const DEFAULT_CAPTURE_FILTER: CaptureFilter = {
  phase: "all", media: "all", room: "all", inspection: "all",
  dateRange: "all",
};

const LEFT_MIN = 180;
const LEFT_MAX = 560;
const LEFT_DEFAULT = 280;
const RIGHT_MIN = 300;
const RIGHT_MAX = 700;
const RIGHT_DEFAULT = 420;

export interface IcProjectDetailPageOptions {
  container: HTMLElement;
  project: IcProject;
  onBack: () => void;
  embed3D?: (localProjectId: string, host: HTMLElement) => Promise<() => void>;
  embed3DFromUrl?: (
    meshUrl: string,
    host: HTMLElement,
    opts?: { localProjectId?: string; icProjectId?: string },
  ) => Promise<() => void>;
  toast?: (msg: string, kind?: "info" | "error") => void;
}

let _shimmerStyleInjected = false;
function _ensureShimmerStyle(): void {
  if (_shimmerStyleInjected) return;
  _shimmerStyleInjected = true;
  const s = document.createElement("style");
  s.textContent = `
@keyframes ic-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.ic-thumb-shimmer {
  position:absolute;inset:0;
  background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
  background-size: 400% 100%;
  animation: ic-shimmer 1.4s linear infinite;
}
@keyframes ic-spin {
  to { transform: rotate(360deg); }
}
.ic-spinner {
  width:32px;height:32px;border-radius:50%;
  border:3px solid rgba(255,255,255,.12);
  border-top-color:rgba(255,255,255,.5);
  animation: ic-spin .8s linear infinite;
}`;
  document.head.appendChild(s);
}

export class IcProjectDetailPage {
  private root: HTMLElement;
  private opts: IcProjectDetailPageOptions;
  private project: IcProject;
  private manifest: Manifest | null = null;
  private manifestVersion = 0;
  private manifestUpdatedAt: string | null = null;
  private local3D: IcLocal3DStatus | null = null;
  private activeTab: TabKey = "overview";
  private tabButtons = new Map<TabKey, HTMLButtonElement>();

  // Layout elements
  private split!: HTMLElement;
  private leftPane!: HTMLElement;
  private leftDragHandle!: HTMLElement;
  private centerPane!: HTMLElement;
  private rightDragHandle!: HTMLElement;
  private rightPane!: HTMLElement;
  private rightBody!: HTMLElement;
  private rightContent!: HTMLElement;
  private expandLeftFab!: HTMLButtonElement;
  private expandRightFab!: HTMLButtonElement;
  private leftCollapsed = false;
  private rightCollapsed = false;
  private leftWidth = LEFT_DEFAULT;
  private rightWidth = RIGHT_DEFAULT;

  // Captures sidebar state
  private allCaptures: ManifestCapture[] = [];
  private roomById = new Map<string, ManifestRoom>();
  // Job statuses keyed by source capture_id — refreshed on every poll cycle
  private captureJobsBySourceId = new Map<string, CaptureJobStatus>();
  // Poll timer — active while any job is pending/processing
  private _jobPollTimer: ReturnType<typeof setTimeout> | null = null;
  private selectedCapture: ManifestCapture | null = null;
  private captureFilter: CaptureFilter = { ...DEFAULT_CAPTURE_FILTER };
  private captureListEl: HTMLElement | null = null;
  private captureFilterBtns = new Map<string, HTMLButtonElement>();
  private captureOpenDropdown: HTMLElement | null = null;
  private captureInspections: ManifestInspection[] = [];
  private captureInspectionById = new Map<string, ManifestInspection>();
  private captureRoomPairs: Array<[string, string]> = [];
  // Lazy-loads thumbnails only when their row scrolls near the viewport.
  // Recreated each time the list is re-rendered.
  private captureThumbObserver: IntersectionObserver | null = null;

  // Center state
  private centerMode: CenterMode = "placeholder";
  private viewer3DDispose: (() => void) | null = null;
  private viewer3DMounting = false;
  private _centerContent!: HTMLElement; // cleared/rebuilt for captures/3D
  private _houseHost!: HTMLElement;     // persistent overlay for house composer
  private _clientPreviewIframe: HTMLIFrameElement | null = null;
  private _clientPreviewCloseBtn: HTMLButtonElement | null = null;

  // Video transcript: hidden video for frame capture + per-capture frame cache
  private _hiddenCaptureVideo: HTMLVideoElement | null = null;
  private _capturePanoViewer: CapturePanoViewer | null = null;
  private _captureFrameCache = new Map<string, Map<number, string>>();

  // House composer state
  private leftMode: LeftMode = "captures";
  private houseComposer: HouseComposerView | null = null;
  private _houseTab: HouseTab | null = null;

  // Streetview modal state
  private _svModal!: HTMLElement;
  private _svPhotoA!: HTMLImageElement;
  private _svPhotoB!: HTMLImageElement;
  private _svTitle!: HTMLElement;
  private _svInfo!: HTMLElement;
  private _svPrevBtn!: HTMLButtonElement;
  private _svNextBtn!: HTMLButtonElement;
  private _svActivePhoto: "a" | "b" = "a";
  private _svTransitioning = false;
  private _svState: { roomIndex: number; nodeId: number } | null = null;

  // ArUco photo modal state
  private _photoModal!: HTMLElement;
  private _photoModalImg!: HTMLImageElement;
  private _photoModalTitle!: HTMLElement;
  private _anchoredPhotos: AnchoredPhoto[] = [];

  constructor(opts: IcProjectDetailPageOptions) {
    this.opts = opts;
    this.project = opts.project;
    this.root = opts.container;
    this.root.innerHTML = "";
    this.root.style.cssText =
      "display:flex;flex-direction:column;height:100%;overflow:hidden;position:relative;";

    _ensureShimmerStyle();
    this.buildLayout();
    this.refresh();
  }

  // ── Layout construction ──────────────────────────────────────────────────

  private buildLayout(): void {
    this.split = document.createElement("div");
    this.split.style.cssText = "flex:1 1 0;min-height:0;display:flex;overflow:hidden;";

    // Left pane
    this.leftPane = document.createElement("div");
    this.leftPane.style.cssText = `
      width:${this.leftWidth}px;flex-shrink:0;
      display:flex;flex-direction:column;
      background:var(--bg);overflow:hidden;
    `;
    this.split.appendChild(this.leftPane);

    // Left resize handle
    this.leftDragHandle = document.createElement("div");
    this.leftDragHandle.style.cssText =
      "width:4px;cursor:col-resize;background:transparent;flex-shrink:0;transition:background 120ms;";
    this.leftDragHandle.onmouseenter = () =>
      (this.leftDragHandle.style.background = "var(--accent)");
    this.leftDragHandle.onmouseleave = () =>
      (this.leftDragHandle.style.background = "transparent");
    this._attachLeftResize();
    this.split.appendChild(this.leftDragHandle);

    // Center pane
    this.centerPane = document.createElement("div");
    this.centerPane.style.cssText =
      "flex:1 1 0;min-width:0;display:flex;flex-direction:column;background:#000;overflow:hidden;position:relative;";

    // Layer 1: regular content (captures, 3D viewer, placeholder)
    this._centerContent = document.createElement("div");
    this._centerContent.style.cssText = "flex:1 1 0;min-height:0;display:flex;flex-direction:column;";
    this.centerPane.appendChild(this._centerContent);

    // Layer 2: house composer — persistent overlay, hidden until house mode
    this._houseHost = document.createElement("div");
    this._houseHost.style.cssText = "position:absolute;inset:0;display:none;transition:right 0.2s;";
    this.centerPane.appendChild(this._houseHost);

    // Layer 3: streetview modal — right 60%, shown when a streetview marker is clicked
    this._svModal = document.createElement("div");
    this._svModal.style.cssText =
      "position:absolute;right:0;top:0;bottom:0;width:60%;display:none;" +
      "flex-direction:column;background:#0d0f12;z-index:20;border-left:1px solid #2a2d35;";

    const svHeader = document.createElement("div");
    svHeader.style.cssText =
      "display:flex;align-items:center;gap:8px;padding:8px 12px;" +
      "border-bottom:1px solid #2a2d35;flex-shrink:0;";

    this._svTitle = document.createElement("span");
    this._svTitle.style.cssText =
      "flex:1;font-size:13px;font-weight:600;color:#e0e0e0;" +
      "overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";

    this._svPrevBtn = document.createElement("button");
    this._svPrevBtn.textContent = "‹";
    this._svPrevBtn.style.cssText =
      "width:28px;height:28px;border:none;border-radius:4px;" +
      "background:#1e2028;color:#ccc;cursor:pointer;font-size:18px;line-height:1;";
    this._svPrevBtn.onclick = () => this._svNavigate(-1);

    this._svInfo = document.createElement("span");
    this._svInfo.style.cssText = "font-size:12px;color:#888;white-space:nowrap;";

    this._svNextBtn = document.createElement("button");
    this._svNextBtn.textContent = "›";
    this._svNextBtn.style.cssText =
      "width:28px;height:28px;border:none;border-radius:4px;" +
      "background:#1e2028;color:#ccc;cursor:pointer;font-size:18px;line-height:1;";
    this._svNextBtn.onclick = () => this._svNavigate(1);

    const svCloseBtn = document.createElement("button");
    svCloseBtn.textContent = "×";
    svCloseBtn.style.cssText =
      "width:28px;height:28px;border:none;border-radius:4px;" +
      "background:transparent;color:#888;cursor:pointer;font-size:18px;line-height:1;";
    svCloseBtn.onclick = () => this._closeSvModal();

    svHeader.append(this._svTitle, this._svPrevBtn, this._svInfo, this._svNextBtn, svCloseBtn);

    const svPhotoWrap = document.createElement("div");
    svPhotoWrap.style.cssText = "flex:1;position:relative;overflow:hidden;background:#000;";

    const imgStyle =
      "position:absolute;inset:0;width:100%;height:100%;object-fit:contain;transition:opacity 0.25s;";
    this._svPhotoA = document.createElement("img");
    this._svPhotoA.style.cssText = imgStyle;
    this._svPhotoB = document.createElement("img");
    this._svPhotoB.style.cssText = imgStyle + "opacity:0;";

    svPhotoWrap.append(this._svPhotoA, this._svPhotoB);
    this._svModal.append(svHeader, svPhotoWrap);
    this.centerPane.appendChild(this._svModal);

    // ArUco photo modal — same right-panel style as _svModal
    this._photoModal = document.createElement("div");
    this._photoModal.style.cssText =
      "position:absolute;right:0;top:0;bottom:0;width:60%;display:none;" +
      "flex-direction:column;background:#0d0f12;z-index:20;border-left:1px solid #2a2d35;";

    const pmHeader = document.createElement("div");
    pmHeader.style.cssText =
      "display:flex;align-items:center;gap:8px;padding:8px 12px;" +
      "border-bottom:1px solid #2a2d35;flex-shrink:0;";

    this._photoModalTitle = document.createElement("span");
    this._photoModalTitle.style.cssText =
      "flex:1;font-size:13px;font-weight:600;color:#e0e0e0;" +
      "overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";

    const pmCloseBtn = document.createElement("button");
    pmCloseBtn.textContent = "×";
    pmCloseBtn.style.cssText =
      "width:28px;height:28px;border:none;border-radius:4px;" +
      "background:transparent;color:#888;cursor:pointer;font-size:18px;line-height:1;";
    pmCloseBtn.onclick = () => this._closePhotoModal();

    pmHeader.append(this._photoModalTitle, pmCloseBtn);

    const pmImgWrap = document.createElement("div");
    pmImgWrap.style.cssText = "flex:1;position:relative;overflow:hidden;background:#000;";

    this._photoModalImg = document.createElement("img");
    this._photoModalImg.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;object-fit:contain;";

    pmImgWrap.appendChild(this._photoModalImg);
    this._photoModal.append(pmHeader, pmImgWrap);
    this.centerPane.appendChild(this._photoModal);

    this.split.appendChild(this.centerPane);

    // Right resize handle
    this.rightDragHandle = document.createElement("div");
    this.rightDragHandle.style.cssText =
      "width:4px;cursor:col-resize;background:transparent;flex-shrink:0;transition:background 120ms;";
    this.rightDragHandle.onmouseenter = () =>
      (this.rightDragHandle.style.background = "var(--accent)");
    this.rightDragHandle.onmouseleave = () =>
      (this.rightDragHandle.style.background = "transparent");
    this._attachRightResize();
    this.split.appendChild(this.rightDragHandle);

    // Right pane
    this.rightPane = document.createElement("div");
    this.rightPane.style.cssText = `
      width:${this.rightWidth}px;flex-shrink:0;
      display:grid;grid-template-rows:auto minmax(0,1fr);
      border-left:1px solid var(--border);background:var(--bg);
      min-height:0;overflow:hidden;
    `;
    this.rightPane.appendChild(this.makeTabBar());
    this.rightBody = document.createElement("div");
    this.rightBody.style.cssText = "min-height:0;overflow-y:auto;";
    this.rightContent = document.createElement("div");
    this.rightBody.appendChild(this.rightContent);
    this.rightPane.appendChild(this.rightBody);
    this.split.appendChild(this.rightPane);

    this.root.appendChild(this.split);
    this.root.appendChild(this.makeExpandLeftFab());
    this.root.appendChild(this.makeExpandRightFab());

    // Initial center placeholder (shows before refresh() completes)
    this.renderCenterDefault();
  }

  // ── Resize ──────────────────────────────────────────────────────────────

  private _attachLeftResize(): void {
    let startX = 0, startW = 0;
    const onMove = (e: PointerEvent) => {
      const w = Math.min(LEFT_MAX, Math.max(LEFT_MIN, startW + (e.clientX - startX)));
      this.leftWidth = w;
      this.leftPane.style.width = w + "px";
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    this.leftDragHandle.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      startX = e.clientX;
      startW = this.leftPane.offsetWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    });
  }

  private _attachRightResize(): void {
    let startX = 0, startW = 0;
    const onMove = (e: PointerEvent) => {
      const delta = startX - e.clientX;
      const w = Math.min(RIGHT_MAX, Math.max(RIGHT_MIN, startW + delta));
      this.rightWidth = w;
      this.rightPane.style.width = w + "px";
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    this.rightDragHandle.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      startX = e.clientX;
      startW = this.rightPane.offsetWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    });
  }

  // ── Collapse / expand ────────────────────────────────────────────────────

  private makeExpandLeftFab(): HTMLElement {
    this.expandLeftFab = document.createElement("button");
    this.expandLeftFab.className = "btn";
    this.expandLeftFab.textContent = "⏵";
    this.expandLeftFab.title = "Show captures";
    this.expandLeftFab.style.cssText =
      "position:absolute;top:8px;left:8px;display:none;z-index:5;padding:4px 10px;font-size:14px;";
    this.expandLeftFab.onclick = () => this.toggleLeftPane();
    return this.expandLeftFab;
  }

  private makeExpandRightFab(): HTMLElement {
    this.expandRightFab = document.createElement("button");
    this.expandRightFab.className = "btn";
    this.expandRightFab.textContent = "⏴";
    this.expandRightFab.title = "Show manifest panel";
    this.expandRightFab.style.cssText =
      "position:absolute;top:8px;right:8px;display:none;z-index:5;padding:4px 10px;font-size:14px;";
    this.expandRightFab.onclick = () => this.toggleRightPane();
    return this.expandRightFab;
  }

  private toggleLeftPane(): void {
    this.leftCollapsed = !this.leftCollapsed;
    if (this.leftCollapsed) {
      this.leftPane.style.display = "none";
      this.leftDragHandle.style.display = "none";
      this.expandLeftFab.style.display = "";
    } else {
      this.leftPane.style.display = "flex";
      this.leftDragHandle.style.display = "";
      this.expandLeftFab.style.display = "none";
    }
  }

  private toggleRightPane(): void {
    this.rightCollapsed = !this.rightCollapsed;
    if (this.rightCollapsed) {
      this.rightPane.style.display = "none";
      this.rightDragHandle.style.display = "none";
      this.expandRightFab.style.display = "";
    } else {
      this.rightPane.style.display = "grid";
      this.rightDragHandle.style.display = "";
      this.expandRightFab.style.display = "none";
    }
  }

  // ── Left pane: Captures sidebar ──────────────────────────────────────────

  private renderCapturesSidebar(): void {
    this.leftPane.innerHTML = "";
    this.captureListEl = null;
    

    // Mode tab bar (Captures | House)
    this.leftPane.appendChild(this._makeLeftModeBar());

    if (this.leftMode === "house") {
      this._renderHouseSidebar();
      return;
    }

    if (this.allCaptures.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText =
        "padding:24px 12px;color:var(--text-muted);font-size:12px;text-align:center;line-height:1.55;";
      empty.textContent = this.manifest
        ? "No captures recorded yet."
        : "No manifest — captures appear once mobile uploads.";
      this.leftPane.appendChild(empty);
      return;
    }

    // Compact filter bar
    this.leftPane.appendChild(this.renderSidebarFilterBar());

    // Scrollable list
    this.captureListEl = document.createElement("div");
    this.captureListEl.style.cssText = "flex:1 1 0;overflow-y:auto;min-height:0;";
    this.leftPane.appendChild(this.captureListEl);
    this.renderCaptureList();

    // Footer: upload + optional download
    const photoCount = this.allCaptures.filter(
      (c) => c.media_type === "photo" && c.uploaded,
    ).length;
    const footer = document.createElement("div");
    footer.style.cssText =
      "flex-shrink:0;padding:10px 12px;border-top:1px solid var(--border);background:var(--surface);display:flex;flex-direction:column;gap:6px;";

    const _makeFooterBtn = (label: string): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.textContent = label;
      btn.style.cssText = `
        display:block;width:100%;box-sizing:border-box;
        padding:7px 12px;text-align:center;
        background:var(--surface);border:1px solid var(--border);
        border-radius:6px;color:var(--text);font-size:12px;
        cursor:pointer;transition:border-color 120ms,color 120ms;
      `;
      btn.onmouseenter = () => {
        if (!btn.disabled) { btn.style.borderColor = "var(--accent)"; btn.style.color = "var(--accent)"; }
      };
      btn.onmouseleave = () => {
        if (!btn.disabled) { btn.style.borderColor = "var(--border)"; btn.style.color = "var(--text)"; }
      };
      return btn;
    };

    const upBtn = _makeFooterBtn("↑ Upload file");
    upBtn.onclick = () => this._openUploadModal();
    footer.appendChild(upBtn);

    if (photoCount > 0) {
      const label = `↓ Download ${photoCount} photo${photoCount !== 1 ? "s" : ""} (.zip)`;
      const dlBtn = _makeFooterBtn(label);
      dlBtn.onclick = async () => {
        dlBtn.disabled = true;
        dlBtn.textContent = "Preparing…";
        dlBtn.style.borderColor = "var(--border)";
        dlBtn.style.color = "var(--text-muted)";
        try {
          const blobUrl = await api.ic.downloadPhotosZip(this.project.project_id);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = `photos_${this.project.project_id.slice(0, 8)}.zip`;
          a.click();
          URL.revokeObjectURL(blobUrl);
        } catch (err) {
          this.opts.toast?.(`Download failed: ${(err as Error).message}`, "error");
        } finally {
          dlBtn.disabled = false;
          dlBtn.textContent = label;
          dlBtn.style.color = "var(--text)";
        }
      };
      footer.appendChild(dlBtn);
    }

    this.leftPane.appendChild(footer);
  }

  private _openUploadModal(): void {
    const inspections = this.captureInspections;
    if (!inspections.length) {
      this.opts.toast?.("No inspections found — create one in the mobile app first.", "error");
      return;
    }

    const backdrop = document.createElement("div");
    backdrop.style.cssText =
      "position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;";

    const card = document.createElement("div");
    card.style.cssText =
      "background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:24px;width:360px;max-width:90vw;display:flex;flex-direction:column;gap:14px;font:13px/1.4 system-ui,sans-serif;color:var(--text);box-shadow:0 16px 48px rgba(0,0,0,0.5);";

    const title = document.createElement("div");
    title.textContent = "Upload file";
    title.style.cssText = "font-size:15px;font-weight:600;";
    card.appendChild(title);

    const _row = (labelText: string, input: HTMLElement): HTMLElement => {
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;flex-direction:column;gap:4px;";
      const lbl = document.createElement("label");
      lbl.textContent = labelText;
      lbl.style.cssText = "font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;color:var(--text-muted);";
      wrap.appendChild(lbl);
      wrap.appendChild(input);
      return wrap;
    };

    const _select = (options: Array<[string, string]>, value?: string): HTMLSelectElement => {
      const sel = document.createElement("select");
      sel.style.cssText =
        "width:100%;padding:7px 8px;background:var(--bg);border:1px solid var(--border);border-radius:6px;color:var(--text);font-size:13px;";
      for (const [v, label] of options) {
        const opt = document.createElement("option");
        opt.value = v;
        opt.textContent = label;
        if (value && v === value) opt.selected = true;
        sel.appendChild(opt);
      }
      return sel;
    };

    // File
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*,video/*,audio/*,.glb,.usdz,.obj,.ply";
    fileInput.style.cssText =
      "width:100%;padding:6px 0;font-size:13px;color:var(--text);background:none;border:none;";
    card.appendChild(_row("File", fileInput));

    // Inspection
    const inspOpts: Array<[string, string]> = inspections.map((ins) => [
      ins.id,
      `${ins.type} — ${ins.date} (${ins.status})`,
    ]);
    const inspSel = _select(inspOpts);
    card.appendChild(_row("Inspection", inspSel));

    // Room (optional)
    const roomOpts: Array<[string, string]> = [["", "— No room —"], ...this.captureRoomPairs];
    const roomSel = _select(roomOpts);
    card.appendChild(_row("Room (optional)", roomSel));

    // Phase
    const phaseOpts: Array<[string, string]> = [
      ["before", "Before"],
      ["after", "After"],
      ["arrival", "Arrival"],
      ["exterior", "Exterior"],
      ["equipment", "Equipment"],
      ["meter", "Meter"],
      ["scope", "Scope"],
      ["during", "During"],
      ["resolution", "Resolution"],
    ];
    const phaseSel = _select(phaseOpts, "before");
    card.appendChild(_row("Phase", phaseSel));

    // Media type (auto-detected, overridable)
    const mediaOpts: Array<[string, string]> = [
      ["photo", "Photo"],
      ["video", "Video"],
      ["audio", "Audio"],
    ];
    const mediaSel = _select(mediaOpts, "photo");
    fileInput.onchange = () => {
      const f = fileInput.files?.[0];
      if (!f) return;
      const mt = f.type;
      if (mt.startsWith("image/")) mediaSel.value = "photo";
      else if (mt.startsWith("video/")) mediaSel.value = "video";
      else if (mt.startsWith("audio/")) mediaSel.value = "audio";
    };
    card.appendChild(_row("Type", mediaSel));

    // Intent (optional)
    const intentOpts: Array<[string, string]> = [
      ["", "— None —"],
      ["overview", "Overview"],
      ["closeup", "Close-up"],
      ["context", "Context"],
      ["qr", "QR code"],
    ];
    const intentSel = _select(intentOpts);
    card.appendChild(_row("Intent (optional)", intentSel));

    // Error line
    const errorEl = document.createElement("div");
    errorEl.style.cssText = "font-size:12px;color:#e05c5c;min-height:16px;";
    card.appendChild(errorEl);

    // Buttons row
    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:8px;justify-content:flex-end;margin-top:4px;";

    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.cssText =
      "padding:7px 16px;background:none;border:1px solid var(--border);border-radius:6px;color:var(--text);font-size:13px;cursor:pointer;";
    cancelBtn.onclick = () => backdrop.remove();

    const submitBtn = document.createElement("button");
    submitBtn.textContent = "Upload";
    submitBtn.style.cssText =
      "padding:7px 16px;background:var(--accent);border:none;border-radius:6px;color:#fff;font-size:13px;font-weight:600;cursor:pointer;";

    // Progress bar (hidden until upload starts)
    const progressWrap = document.createElement("div");
    progressWrap.style.cssText =
      "display:none;height:3px;background:var(--border);border-radius:2px;overflow:hidden;margin-top:-6px;";
    const progressBar = document.createElement("div");
    progressBar.style.cssText =
      "height:100%;width:0%;background:var(--accent);transition:width 200ms ease;";
    progressWrap.appendChild(progressBar);
    card.insertBefore(progressWrap, btnRow);

    let progressTimer: ReturnType<typeof setInterval> | null = null;
    const _startProgress = () => {
      progressWrap.style.display = "block";
      let pct = 0;
      progressTimer = setInterval(() => {
        pct = Math.min(pct + (pct < 60 ? 4 : pct < 85 ? 1.5 : 0.3), 92);
        progressBar.style.width = `${pct}%`;
      }, 200);
    };
    const _finishProgress = () => {
      if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
      progressBar.style.width = "100%";
      setTimeout(() => { progressWrap.style.display = "none"; progressBar.style.width = "0%"; }, 400);
    };

    submitBtn.onclick = async () => {
      errorEl.textContent = "";
      const file = fileInput.files?.[0];
      if (!file) { errorEl.textContent = "Please select a file."; return; }
      const inspectionId = inspSel.value;
      if (!inspectionId) { errorEl.textContent = "Please select an inspection."; return; }

      submitBtn.disabled = true;
      cancelBtn.disabled = true;
      submitBtn.textContent = "Uploading…";
      _startProgress();

      const roomId = roomSel.value || undefined;
      const room = roomId ? this.roomById.get(roomId) : undefined;

      try {
        await api.ic.uploadCapture(this.project.project_id, {
          file,
          inspectionId,
          phase: phaseSel.value,
          mediaType: mediaSel.value,
          intent: intentSel.value || undefined,
          roomId,
          roomName: room?.name,
        });
        _finishProgress();
        backdrop.remove();
        this.opts.toast?.("File uploaded successfully.", "info");
        await this.refresh();
      } catch (err) {
        _finishProgress();
        errorEl.textContent = (err as Error).message || "Upload failed.";
        submitBtn.disabled = false;
        cancelBtn.disabled = false;
        submitBtn.textContent = "Upload";
      }
    };

    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(submitBtn);
    card.appendChild(btnRow);

    backdrop.appendChild(card);
    backdrop.onclick = (e) => { if (e.target === backdrop) backdrop.remove(); };
    document.body.appendChild(backdrop);
  }

  private _makeLeftModeBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.style.cssText =
      "display:flex;align-items:center;border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;";

    const modes: Array<{ key: LeftMode; label: string }> = [
      { key: "captures", label: "Captures" },
      { key: "house", label: "House" },
    ];

    for (const m of modes) {
      const btn = document.createElement("button");
      btn.textContent = m.label;
      const active = this.leftMode === m.key;
      btn.style.cssText = `
        flex:1;padding:8px 4px;background:none;border:none;
        border-bottom:2px solid ${active ? "var(--accent)" : "transparent"};
        color:${active ? "var(--accent)" : "var(--text-muted)"};
        font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;
        cursor:pointer;transition:color 120ms,border-color 120ms;
      `;
      btn.onmouseenter = () => {
        if (this.leftMode !== m.key) btn.style.color = "var(--text)";
      };
      btn.onmouseleave = () => {
        if (this.leftMode !== m.key) btn.style.color = "var(--text-muted)";
      };
      btn.onclick = () => this._setLeftMode(m.key);
      bar.appendChild(btn);
    }

    const collapseBtn = document.createElement("button");
    collapseBtn.title = "Hide captures";
    collapseBtn.textContent = "⏴";
    collapseBtn.style.cssText =
      "background:none;border:none;cursor:pointer;color:var(--text-muted);font-size:13px;padding:4px 8px;border-radius:4px;transition:color 120ms;flex-shrink:0;margin-bottom:-2px;";
    collapseBtn.onmouseenter = () => (collapseBtn.style.color = "var(--text)");
    collapseBtn.onmouseleave = () => (collapseBtn.style.color = "var(--text-muted)");
    collapseBtn.onclick = () => this.toggleLeftPane();
    bar.appendChild(collapseBtn);

    return bar;
  }

  private _setLeftMode(mode: LeftMode): void {
    if (this.leftMode === mode) return;
    this.leftMode = mode;

    if (mode === "house") {
      // Dispose any open 3D viewer so center is free for the composer.
      if (this.viewer3DDispose) {
        this.viewer3DDispose();
        this.viewer3DDispose = null;
        this.centerMode = "placeholder";
      }
      this._mountHouseComposer();
    } else {
      this._hideHouseComposer();
      this.renderCenter();
    }

    this.renderCapturesSidebar();
  }

  private _mountHouseComposer(): void {
    this._houseHost.style.display = "block";
    if (this.houseComposer) return; // already initialised — just show

    this.houseComposer = new HouseComposerView(this._houseHost);

    this.houseComposer.getPanoramaBlobUrl = (captureId, filename) =>
      this._captureMediaBlobUrl(captureId, filename);

    this.houseComposer.onStreetViewNodeClick = (roomIndex, nodeId) =>
      this._openSvModal(roomIndex, nodeId);

    // Load existing composition from backend (first mount only).
    void (async () => {
      try {
        const comp = await api.ic.getHouseComposition(this.project.project_id);
        await this._populateComposerFromComposition(comp);
      } catch { /* no composition yet */ }
      // Load ArUco marker anchors — rendered as 3D labels once a room GLB loads.
      try {
        const anchors = await api.ic.getArUcoAnchors(this.project.project_id);
        this._anchoredPhotos = anchors.anchored_photos.map((p) => ({
          capture_id: p.capture_id,
          marker_id: p.marker_id,
          filename: p.filename,
        }));
        if (anchors.markers.length && this.houseComposer) {
          this.houseComposer.onArUcoMarkersChange = () => this._houseTab?.renderAnchors();
          this.houseComposer.addArUcoMarkers(anchors.markers);
        }
      } catch { /* no ArUco data yet */ }
    })();
  }

  private _hideHouseComposer(): void {
    this._houseHost.style.display = "none";
    this._closeSvModal();
  }

  // ── Streetview modal ────────────────────────────────────────────────────────

  private _openSvModal(roomIndex: number, nodeId: number): void {
    const room = this.houseComposer?.getRooms()[roomIndex];
    if (!room?.svMarkers) return;
    this._svState = { roomIndex, nodeId };
    this._svTitle.textContent = "Streetview";
    this._svModal.style.display = "flex";
    this._houseHost.style.right = "60%";
    room.svMarkers.setActiveNode(nodeId);
    this._svShowNode(nodeId, false);
  }

  private _closeSvModal(): void {
    if (!this._svState) return;
    const room = this.houseComposer?.getRooms()[this._svState.roomIndex];
    room?.svMarkers?.setActiveNode(null);
    this._svState = null;
    this._svModal.style.display = "none";
    this._houseHost.style.right = "0";
  }

  private _openPhotoModal(captureId: string, filename: string): void {
    this._photoModalTitle.textContent = filename.split("/").pop() ?? filename;
    this._photoModalImg.src = "";
    this._photoModal.style.display = "flex";
    this._houseHost.style.right = "60%";
    api.ic.captureMediaMediumBlobUrl(this.project.project_id, captureId)
      .then((url) => { this._photoModalImg.src = url; })
      .catch(() => {});
  }

  private _closePhotoModal(): void {
    this._photoModal.style.display = "none";
    this._houseHost.style.right = "0";
    this._photoModalImg.src = "";
  }

  private _svShowNode(nodeId: number, crossfade: boolean): void {
    if (!this._svState) return;
    const room = this.houseComposer?.getRooms()[this._svState.roomIndex];
    if (!room?.svMarkers) return;
    this._svState.nodeId = nodeId;

    const ids = room.svMarkers.sortedNodeIds;
    const seqIdx = ids.indexOf(nodeId);
    this._svInfo.textContent = `Node ${seqIdx + 1} / ${ids.length}`;
    this._svPrevBtn.disabled = seqIdx <= 0;
    this._svNextBtn.disabled = seqIdx >= ids.length - 1;

    room.svMarkers.setActiveNode(nodeId);
    const imgUrl = `${room.svImageBaseUrl}/${nodeId}.jpg${room.svImageUrlQuery}`;

    const curr = this._svActivePhoto === "a" ? this._svPhotoA : this._svPhotoB;
    const next = this._svActivePhoto === "a" ? this._svPhotoB : this._svPhotoA;

    if (crossfade && !this._svTransitioning) {
      this._svTransitioning = true;
      next.onload = () => {
        next.style.opacity = "1";
        curr.style.opacity = "0";
        this._svActivePhoto = this._svActivePhoto === "a" ? "b" : "a";
        setTimeout(() => { this._svTransitioning = false; }, 300);
      };
      next.src = imgUrl;
    } else {
      curr.src = imgUrl;
      curr.style.opacity = "1";
      next.style.opacity = "0";
    }
  }

  private _svNavigate(dir: -1 | 1): void {
    if (!this._svState) return;
    const room = this.houseComposer?.getRooms()[this._svState.roomIndex];
    if (!room?.svMarkers) return;
    const ids = room.svMarkers.sortedNodeIds;
    const idx = ids.indexOf(this._svState.nodeId);
    const next = ids[idx + dir];
    if (next !== undefined) this._svShowNode(next, true);
  }

  private async _populateComposerFromComposition(comp: HouseComposition): Promise<void> {
    if (!this.houseComposer) return;

    // Initialize floors — legacy compositions (no floors) get a single default floor.
    const isLegacy = !comp.floors?.length;
    const floors: HouseFloor[] = isLegacy && comp.rooms.length
      ? [{ id: crypto.randomUUID(), label: "Floor 1", index: 0, y_offset: 0 }]
      : (comp.floors ?? []);
    this.houseComposer.setFloors(floors);

    // Panoramas: no URL needed — add immediately
    for (const entry of comp.panoramas ?? []) {
      this.houseComposer.addPanorama(entry);
    }

    // Measurements and notes: restore annotations
    for (const entry of comp.measurements ?? []) {
      this.houseComposer.addMeasurementEntry(entry);
    }
    for (const entry of comp.notes ?? []) {
      this.houseComposer.addNoteEntry(entry);
    }

    // Rooms phase 1: register all immediately (shows loading bars).
    // Backfill walls_capture_id / floorplan_capture_id if the saved composition
    // predates those features.
    const enrichedRooms = comp.rooms.map((entry) => {
      const roomId = this.allCaptures.find((c) => c.id === entry.capture_id)?.room_id;
      let updated = { ...entry };
      // Legacy migration: assign all rooms to the default floor if no floor_id set.
      if (!updated.floor_id && floors[0]) updated.floor_id = floors[0].id;
      if (!updated.walls_capture_id && roomId) {
        const wallsCap = this.allCaptures.find(
          (c) => c.room_id === roomId && c.media_type === "roomplan_model_glb",
        );
        if (wallsCap) {
          updated.walls_capture_id = wallsCap.id;
          updated.walls_filename = wallsCap.filename;
        }
      }
      if (!updated.floorplan_capture_id && roomId) {
        const fpCap = this.allCaptures.find(
          (c) => c.room_id === roomId && c.media_type === "floorplan_svg",
        );
        if (fpCap) {
          updated.floorplan_capture_id = fpCap.id;
          updated.floorplan_filename = fpCap.filename;
        }
      }
      return updated;
    });
    const roomIndices = enrichedRooms.map((entry) => this.houseComposer!.registerRoom(entry));

    // Load each room's model FIRST, then its walls — sequential per room so that
    // state.centerOffset (set by loadRoomModel) is always available when
    // loadRoomWalls needs to auto-position walls that have no saved transform.
    // Different rooms still load in parallel with each other.
    // Floorplans are independent and load in parallel as well.
    await Promise.all([
      ...enrichedRooms.map(async (entry, i) => {
        const modelUrl = await this._captureMediaBlobUrl(entry.capture_id, entry.filename);
        if (modelUrl && this.houseComposer) {
          await this.houseComposer.loadRoomModel(roomIndices[i], modelUrl);
        }
        if (entry.walls_capture_id && entry.walls_filename) {
          const wallsUrl = await this._captureMediaBlobUrl(entry.walls_capture_id, entry.walls_filename);
          if (wallsUrl && this.houseComposer) {
            await this.houseComposer.loadRoomWalls(roomIndices[i], wallsUrl);
          }
        }
        // Auto-detect streetview ZIP from room model filename (fire-and-forget)
        void this._tryAutoLoadStreetView(roomIndices[i]);
      }),
      ...(comp.floorplans ?? []).map(async (entry) => {
        const url = await this._captureMediaBlobUrl(entry.capture_id, entry.filename);
        if (url && this.houseComposer) this.houseComposer.addFloorplan(entry, url);
      }),
    ]);
  }

  private _svZipFilename(roomFilename: string): string {
    const stem = roomFilename.replace(/\.[^.]+$/, "");
    const suffixes = ["_mesh", "_cloud", "_dense", "_sparse"];
    let prefix = stem;
    for (const suf of suffixes) {
      if (stem.endsWith(suf)) { prefix = stem.slice(0, -suf.length); break; }
    }
    return `${prefix}_streetview.zip`;
  }

  private async _tryAutoLoadStreetView(roomIndex: number): Promise<void> {
    if (!this.houseComposer) return;
    const room = this.houseComposer.getRooms()[roomIndex];
    if (!room) return;
    const zipFilename = this._svZipFilename(room.entry.filename);
    const manifestUrl = api.icStreetview.manifestUrlForCapture(this.project.project_id, zipFilename);
    const imageBaseUrl = api.icStreetview.imageBaseUrl(this.project.project_id);
    const imageUrlQuery = api.icStreetview.imageUrlQueryForCapture(zipFilename);
    this.houseComposer.setRoomStreetViewLoading(roomIndex, true);
    try {
      const resp = await fetch(manifestUrl);
      if (!resp.ok) {
        // 404 = no streetview for this room — silently skip, don't show error row
        this.houseComposer.setRoomStreetViewLoading(roomIndex, false);
        return;
      }
      const manifest = await resp.json() as StreetViewManifest;
      this.houseComposer.loadRoomStreetView(roomIndex, manifest, imageBaseUrl, imageUrlQuery);
    } catch (err) {
      this.houseComposer.setRoomStreetViewLoading(roomIndex, false, (err as Error).message);
    }
  }

  private async _captureMediaBlobUrl(captureId: string, filename: string): Promise<string | null> {
    try {
      if (captureId.startsWith("glb:")) return await api.ic.scanFileBlobUrl(this.project.project_id, filename);
      return await api.ic.captureMediaBlobUrl(this.project.project_id, captureId);
    } catch { return null; }
  }

  private async _glbBlobUrlForCapture(capture: ManifestCapture): Promise<string | null> {
    return this._captureMediaBlobUrl(capture.id, capture.filename ?? "");
  }

  private _renderHouseSidebar(): void {
    if (!this.houseComposer) {
      this._mountHouseComposer();
    }

    const host = document.createElement("div");
    host.style.cssText = "flex:1 1 0;min-height:0;overflow:hidden;display:flex;flex-direction:column;";
    this.leftPane.appendChild(host);

    this._houseTab = new HouseTab({
      container: host,
      icProjectId: this.project.project_id,
      composer: this.houseComposer!,
      allCaptures: this.allCaptures,
      roomById: this.roomById,
      onAddItem: async (type, capture) => {
        if (!this.houseComposer) return;
        const room = capture.room_id ? this.roomById.get(capture.room_id) : null;
        const label = room?.name ?? capture.filename ?? capture.id;

        if (type === "room") {
          const identity = [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1];
          const wallsCap = capture.room_id
            ? this.allCaptures.find((c) => c.room_id === capture.room_id && c.media_type === "roomplan_model_glb")
            : null;
          const fpCap = capture.room_id
            ? this.allCaptures.find((c) => c.room_id === capture.room_id && c.media_type === "floorplan_svg")
            : null;
          const entry: HouseRoomEntry = {
            capture_id: capture.id,
            filename: capture.filename ?? "",
            label,
            transform: identity,
            visible: true,
            floor_id: this.houseComposer.getActiveFloorId() ?? (this.houseComposer.getFloors()[0]?.id ?? null),
            walls_capture_id: wallsCap?.id ?? null,
            walls_filename: wallsCap?.filename ?? null,
            floorplan_capture_id: fpCap?.id ?? null,
            floorplan_filename: fpCap?.filename ?? null,
          };
          const url = await this._glbBlobUrlForCapture(capture);
          if (!url) { this.opts.toast?.("Failed to load GLB", "error"); return; }
          const roomIndex = this.houseComposer.registerRoom(entry);
          await this.houseComposer.loadRoomModel(roomIndex, url);
          void this._tryAutoLoadStreetView(roomIndex);
        } else if (type === "floorplan") {
          const entry: HouseFloorplanEntry = { capture_id: capture.id, filename: capture.filename ?? "", label, x: 0, y: 0, z: 0, rotation: 0, visible: true };
          const url = await this._glbBlobUrlForCapture(capture);
          if (url) this.houseComposer.addFloorplan(entry, url);
          else this.opts.toast?.("Failed to load floorplan", "error");
        } else if (type === "panorama") {
          const roomCaptureId = capture.room_id
            ? this.houseComposer.getRooms().find(r => {
                const rc = this.allCaptures.find(c => c.id === r.entry.capture_id);
                return rc?.room_id === capture.room_id;
              })?.entry.capture_id ?? null
            : null;
          const entry: HousePanoramaEntry = { capture_id: capture.id, filename: capture.filename ?? "", label, x: 0, y: 1.5, z: 0, yaw: 0, visible: true, mirror: false, room_capture_id: roomCaptureId };
          this.houseComposer.addPanorama(entry);
        }
      },
      onRemoveItem: (type, index) => {
        if (type === "room") {
          if (this._svState?.roomIndex === index) this._closeSvModal();
          this.houseComposer?.removeRoom(index);
        }
        else if (type === "floorplan") this.houseComposer?.removeFloorplan(index);
        else if (type === "panorama") this.houseComposer?.removePanorama(index);
      },
      onShowPanoramaPreview: async (index) => {
        const p = this.houseComposer?.getPanoramas()[index];
        if (!p) return null;
        return this._captureMediaBlobUrl(p.entry.capture_id, p.entry.filename);
      },
      onSave: async () => {
        if (!this.houseComposer) return;
        await api.ic.putHouseComposition(this.project.project_id, this.houseComposer.getEntries());
      },
      onExport: async () => {
        if (!this.houseComposer) return;
        const blob = await this.houseComposer.exportGlb();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `house_${this.project.project_id}.glb`;
        a.click();
        URL.revokeObjectURL(url);
      },
      onGenerateClientLink: async () => {
        return api.ic.generateClientLink(this.project.project_id);
      },
      onLoadWalls: async (roomIndex) => {
        const room = this.houseComposer?.getRooms()[roomIndex];
        if (!room?.entry.walls_capture_id || !room.entry.walls_filename) return;
        const url = await this._captureMediaBlobUrl(
          room.entry.walls_capture_id,
          room.entry.walls_filename,
        );
        if (url) await this.houseComposer?.loadRoomWalls(roomIndex, url);
        else this.opts.toast?.("Failed to load walls", "error");
      },
      onLoadStreetView: async (roomIndex) => {
        await this._tryAutoLoadStreetView(roomIndex);
      },
      onSelectFloorplanSource: async (roomIndex, captureId, filename, mediaType) => {
        // Persist selection in room entry
        const rooms = this.houseComposer?.getRooms();
        if (rooms?.[roomIndex]) {
          rooms[roomIndex].entry.floorplan_capture_id = captureId;
          rooms[roomIndex].entry.floorplan_filename = filename;
          rooms[roomIndex].entry.floorplan_media_type = mediaType;
        }

        const url = await this._captureMediaBlobUrl(captureId, filename);
        if (!url) { this.opts.toast?.("Failed to load floor plan", "error"); return; }

        if (mediaType === "roomplan_json") {
          const data = await fetch(url).then((r) => r.json()).catch(() => null);
          if (data) await this.houseComposer?.loadRoomFloorplanFromJson(roomIndex, data);
          else this.opts.toast?.("Failed to parse plan JSON", "error");
        } else {
          const svgText = await fetch(url).then((r) => r.text()).catch(() => null);
          if (svgText) await this.houseComposer?.loadRoomFloorplan(roomIndex, svgText);
          else this.opts.toast?.("Failed to read floorplan SVG", "error");
        }
      },
      onResetWalls: (roomIndex) => {
        this.houseComposer?.resetWallsTransform(roomIndex);
      },
      onResetFloorplan: (roomIndex) => {
        this.houseComposer?.resetFloorplanTransform(roomIndex);
      },
      onToggleFloorplanMirror: (roomIndex) => {
        this.houseComposer?.toggleRoomFloorplanMirror(roomIndex);
      },
      getAnchoredPhotos: () => this._anchoredPhotos,
      onArUcoPhotoClick: (captureId, filename) => {
        this._openPhotoModal(captureId, filename);
      },
      getThumbUrl: (captureId) =>
        api.ic.captureMediaThumbBlobUrl(this.project.project_id, captureId),
    });
  }

  private renderSidebarFilterBar(): HTMLElement {
    this.captureFilterBtns.clear();

    const wrap = document.createElement("div");
    wrap.style.cssText =
      "padding:8px 10px;border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;display:flex;flex-direction:column;gap:5px;";

    // Pill row
    const pills = document.createElement("div");
    pills.style.cssText = "display:flex;gap:4px;flex-wrap:wrap;";

    const visibleCaptures = this.allCaptures.filter((c) => this._isOperatorVisible(c));

    // Phase
    const phases = [...new Set(visibleCaptures.map((c) => c.phase))].sort();
    pills.appendChild(this._makeSidebarPill("Phase", "phase", [
      ["all", "All phases"],
      ...phases.map((p): [string, string] => [p, p]),
    ]));

    // Room (only when multiple rooms)
    if (this.captureRoomPairs.length > 1) {
      const roomOpts: Array<[string, string]> = [["all", "All rooms"], ...this.captureRoomPairs];
      if (visibleCaptures.some((c) => !c.room_id)) roomOpts.push(["__noroom__", "(no room)"]);
      pills.appendChild(this._makeSidebarPill("Room", "room", roomOpts));
    }

    // Date
    pills.appendChild(this._makeSidebarPill("Date", "dateRange", [
      ["all", "All time"],
      ["today", "Today"],
      ["7d", "Last 7 days"],
      ["30d", "Last 30 days"],
    ]));

    // Media (only when multiple types)
    const medias = [...new Set(visibleCaptures.map((c) => c.media_type))].sort();
    if (medias.length > 1) {
      pills.appendChild(this._makeSidebarPill("Media", "media", [
        ["all", "All"],
        ...medias.map((m): [string, string] => [m, m]),
      ]));
    }

    // Inspection (only when multiple)
    if (this.captureInspections.length > 1) {
      const insOpts: Array<[string, string]> = [["all", "All inspections"]];
      for (const ins of this.captureInspections) {
        const date = ins.date
          ? new Date(ins.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : "—";
        insOpts.push([ins.id, `${ins.type.charAt(0).toUpperCase() + ins.type.slice(1)} · ${date}`]);
      }
      pills.appendChild(this._makeSidebarPill("Insp.", "inspection", insOpts));
    }

    // Clear button — spacer pushes it to the right within the pills row
    const spacer = document.createElement("div");
    spacer.style.cssText = "flex:1;";
    pills.appendChild(spacer);
    const clearBtn = document.createElement("button");
    clearBtn.textContent = "Clear";
    clearBtn.style.cssText =
      "font-size:10px;color:var(--text-muted);background:none;border:none;cursor:pointer;padding:0;flex-shrink:0;";
    clearBtn.onclick = () => {
      this.captureFilter = { ...DEFAULT_CAPTURE_FILTER };
      this._syncCaptureFilterBtns();
      this.renderCaptureList();
    };
    pills.appendChild(clearBtn);

    wrap.appendChild(pills);

    this._syncCaptureFilterBtns();
    return wrap;
  }

  private _makeSidebarPill(
    label: string,
    key: keyof CaptureFilter,
    options: Array<[string, string]>,
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.dataset.label = label;
    btn.style.cssText =
      "padding:3px 8px;border-radius:99px;font-size:10px;cursor:pointer;border:1px solid var(--border);background:transparent;color:var(--text-muted);white-space:nowrap;flex-shrink:0;transition:border-color 80ms,color 80ms;";
    btn.onclick = (e) => {
      e.stopPropagation();
      if (this.captureOpenDropdown) {
        this._closeSidebarDropdown();
        return;
      }
      this._openSidebarDropdown(btn, key, options);
    };
    this.captureFilterBtns.set(key as string, btn);
    return btn;
  }

  private _openSidebarDropdown(
    anchor: HTMLButtonElement,
    key: keyof CaptureFilter,
    options: Array<[string, string]>,
  ): void {
    const rect = anchor.getBoundingClientRect();
    const menu = document.createElement("div");
    menu.style.cssText = `
      position: fixed;
      top: ${rect.bottom + 3}px;
      left: ${rect.left}px;
      z-index: 500;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      box-shadow: 0 8px 24px rgba(0,0,0,.5);
      min-width: ${Math.max(rect.width, 140)}px;
      max-height: 220px;
      overflow-y: auto;
      padding: 4px 0;
    `;

    const currentVal = String((this.captureFilter as Record<string, unknown>)[key as string]);

    for (const [val, display] of options) {
      const isActive = currentVal === val;
      const item = document.createElement("button");
      item.style.cssText = `
        display: flex; align-items: center; gap: 7px;
        width: 100%; padding: 6px 10px;
        font-size: 11px; text-align: left; white-space: nowrap;
        background: ${isActive ? "rgba(255,255,255,.05)" : "transparent"};
        color: ${isActive ? "var(--accent)" : "var(--text)"};
        border: none; cursor: pointer;
      `;
      item.onmouseenter = () => { item.style.background = "rgba(255,255,255,.07)"; };
      item.onmouseleave = () => { item.style.background = isActive ? "rgba(255,255,255,.05)" : "transparent"; };

      const check = document.createElement("span");
      check.textContent = isActive ? "✓" : "";
      check.style.cssText = "font-size:10px;width:10px;flex-shrink:0;color:var(--accent);";
      item.appendChild(check);
      const text = document.createElement("span");
      text.textContent = display;
      item.appendChild(text);

      item.onclick = (e) => {
        e.stopPropagation();
        (this.captureFilter as Record<string, unknown>)[key as string] = val;
        this._syncCaptureFilterBtns();
        this.renderCaptureList();
        this._closeSidebarDropdown();
      };
      menu.appendChild(item);
    }

    document.body.appendChild(menu);
    this.captureOpenDropdown = menu;

    const outsideClick = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node) && e.target !== anchor) {
        this._closeSidebarDropdown();
        document.removeEventListener("click", outsideClick, true);
      }
    };
    setTimeout(() => document.addEventListener("click", outsideClick, true), 0);
    (menu as unknown as Record<string, unknown>)._cleanup =
      () => document.removeEventListener("click", outsideClick, true);
  }

  private _closeSidebarDropdown(): void {
    if (!this.captureOpenDropdown) return;
    const cleanup = (this.captureOpenDropdown as unknown as Record<string, unknown>)._cleanup;
    if (typeof cleanup === "function") cleanup();
    this.captureOpenDropdown.remove();
    this.captureOpenDropdown = null;
  }

  private _syncCaptureFilterBtns(): void {
    const DATE_LABELS: Record<string, string> = { today: "Today", "7d": "7d", "30d": "30d" };

    for (const [key, btn] of this.captureFilterBtns) {
      const val = (this.captureFilter as Record<string, unknown>)[key];
      const isActive = val !== "all" && val !== false;
      const label = btn.dataset.label ?? key;

      if (isActive) {
        let display = String(val);
        if (key === "dateRange") display = DATE_LABELS[display] ?? display;
        else if (key === "inspection") {
          const ins = this.captureInspectionById.get(String(val));
          if (ins) {
            display = ins.date
              ? new Date(ins.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
              : ins.type;
          }
        } else if (key === "room") {
          display = val === "__noroom__"
            ? "none"
            : (this.roomById.get(String(val))?.name?.slice(0, 10) ?? String(val).slice(0, 8));
        }
        btn.textContent = `${label}: ${display} ▾`;
        btn.style.borderColor = "var(--accent)";
        btn.style.color = "var(--accent)";
      } else {
        btn.textContent = `${label} ▾`;
        btn.style.borderColor = "var(--border)";
        btn.style.color = "var(--text-muted)";
      }
    }

  }

  private renderCaptureList(): void {
    if (!this.captureListEl) return;
    this.captureListEl.innerHTML = "";

    // Reset the lazy-load observer for the fresh set of rows.
    this.captureThumbObserver?.disconnect();
    this.captureThumbObserver = new IntersectionObserver(
      (entries, obs) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          obs.unobserve(el);
          const load = (el as unknown as { _loadThumb?: () => void })._loadThumb;
          load?.();
        }
      },
      { root: this.captureListEl, rootMargin: "200px 0px" },
    );

    const f = this.captureFilter;
    const filtered = this.allCaptures
      .filter((c) => {
        if (!this._isOperatorVisible(c)) return false;
        if (f.phase !== "all" && c.phase !== f.phase) return false;
        if (f.media !== "all" && c.media_type !== f.media) return false;
        if (f.room === "__noroom__") {
          if (c.room_id) return false;
        } else if (f.room !== "all") {
          if (c.room_id !== f.room) return false;
        }
        if (f.inspection !== "all" && c.inspection_id !== f.inspection) return false;
        if (f.dateRange !== "all") {
          const captured = new Date(c.captured_at);
          const now = new Date();
          if (f.dateRange === "today") {
            if (captured.toDateString() !== now.toDateString()) return false;
          } else {
            const days = f.dateRange === "7d" ? 7 : 30;
            if (captured.getTime() < now.getTime() - days * 86400000) return false;
          }
        }
        return true;
      })
      .sort((a, b) => (b.captured_at || "").localeCompare(a.captured_at || ""));

    if (filtered.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText =
        "padding:16px;color:var(--text-muted);font-size:11px;text-align:center;";
      empty.textContent = "No captures match.";
      this.captureListEl.appendChild(empty);
      return;
    }

    for (const c of filtered) {
      this.captureListEl.appendChild(this.makeCaptureRow(c));
    }
  }

  private makeCaptureRow(c: ManifestCapture): HTMLElement {
    const isSelected = this.selectedCapture?.id === c.id;
    const row = document.createElement("div");
    row.style.cssText = `
      display:flex;align-items:center;gap:10px;
      padding:8px 10px;cursor:pointer;
      border-left:3px solid ${isSelected ? "var(--accent)" : "transparent"};
      background:${isSelected ? "var(--surface2)" : "transparent"};
      transition:background 80ms,border-color 80ms;
    `;
    row.onclick = () => this.selectCapture(c);

    // Thumbnail — deferred until the row scrolls near the viewport so we
    // don't fire dozens of requests up front.
    const thumb = document.createElement("div");
    thumb.style.cssText =
      "width:52px;height:52px;flex-shrink:0;border-radius:4px;overflow:hidden;background:#111;display:flex;align-items:center;justify-content:center;position:relative;";
    if ((c.media_type === "photo" || c.media_type === "panorama_frame") && c.uploaded) {
      const shimmer = document.createElement("div");
      shimmer.className = "ic-thumb-shimmer";
      thumb.appendChild(shimmer);

      const img = document.createElement("img");
      img.style.cssText = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity 150ms;";
      thumb.appendChild(img);

      if (c.media_type === "panorama_frame") {
        const badge = document.createElement("div");
        badge.style.cssText =
          "position:absolute;bottom:3px;left:3px;font-size:8px;font-weight:700;color:#fff;" +
          "background:rgba(0,0,0,.65);padding:1px 3px;border-radius:2px;letter-spacing:.5px;pointer-events:none;";
        badge.textContent = "360°";
        thumb.appendChild(badge);
      }

      const loadThumb = () => {
        api.ic
          .captureMediaThumbBlobUrl(this.project.project_id, c.id)
          .then((blobUrl) => {
            // Don't revoke — the URL is cached for the page lifetime so
            // re-rendering the list (filter, scroll back) is instant.
            img.src = blobUrl;
            img.onload = () => {
              img.style.opacity = "1";
              shimmer.remove();
            };
          })
          .catch(() => {
            shimmer.remove();
            img.remove();
            const icon = document.createElement("div");
            icon.style.cssText = "font-size:20px;opacity:.4;";
            icon.textContent = MEDIA_ICON[c.media_type] || "?";
            thumb.appendChild(icon);
          });
      };
      (thumb as unknown as { _loadThumb: () => void })._loadThumb = loadThumb;
      this.captureThumbObserver?.observe(thumb);
    } else {
      const icon = document.createElement("div");
      icon.style.cssText = "font-size:22px;opacity:.4;";
      icon.textContent = MEDIA_ICON[c.media_type] || "?";
      thumb.appendChild(icon);
    }

    // Phase dot
    const dot = document.createElement("span");
    dot.style.cssText = `
      position:absolute;bottom:3px;right:3px;
      width:8px;height:8px;border-radius:50%;
      background:${PHASE_COLORS[c.phase] || PHASE_COLORS.other};
      border:1px solid rgba(0,0,0,.5);
    `;
    thumb.appendChild(dot);
    row.appendChild(thumb);

    // Text column
    const info = document.createElement("div");
    info.style.cssText =
      "flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;";

    const topLine = document.createElement("div");
    topLine.style.cssText =
      "font-size:12px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;";
    topLine.textContent =
      c.media_type === "glb"
        ? `GLB scan${c.field_note ? " · " + c.field_note : ""}`
        : c.media_type === "usdz"
          ? `USDZ scan${c.field_note ? " · " + c.field_note : ""}`
          : `${c.phase}${c.intent ? " · " + c.intent : ""}`;
    info.appendChild(topLine);

    const room = this.roomById.get(c.room_id || "");
    const roomLine = document.createElement("div");
    roomLine.style.cssText =
      "font-size:11px;color:var(--text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;";
    roomLine.textContent = room?.name ?? (c.room_id ? "(room)" : "(no room)");
    info.appendChild(roomLine);

    const meta = document.createElement("div");
    meta.style.cssText = "font-size:10px;color:#555;display:flex;gap:6px;";
    const mType = document.createElement("span");
    mType.textContent = c.media_type;
    const mTime = document.createElement("span");
    mTime.textContent = _formatRelative(c.captured_at);
    meta.appendChild(mType);
    meta.appendChild(mTime);
    info.appendChild(meta);

    row.appendChild(info);

    const genBtn = this._makeGenerateButton(c);
    if (genBtn) row.appendChild(genBtn);

    if (c.media_type === "video" && c.transcript_text) {
      const tIcon = document.createElement("span");
      const segCount = c.transcript_segments?.length ?? 0;
      tIcon.title = segCount
        ? `Transcript available · ${segCount} segment${segCount === 1 ? "" : "s"}`
        : "Transcript available";
      tIcon.textContent = "T";
      tIcon.style.cssText =
        "flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;" +
        "width:18px;height:18px;border-radius:3px;font-size:10px;font-weight:700;" +
        "background:var(--accent);color:#fff;letter-spacing:.5px;";
      row.appendChild(tIcon);
    }

    const delBtn = document.createElement("button");
    delBtn.title = "Delete capture";
    delBtn.style.cssText =
      "flex-shrink:0;background:none;border:none;cursor:pointer;padding:4px;" +
      "opacity:0;transition:opacity 120ms;color:var(--text-muted);font-size:14px;line-height:1;";
    delBtn.textContent = "✕";
    row.onmouseenter = () => {
      if (!isSelected) row.style.background = "var(--surface)";
      delBtn.style.opacity = "1";
    };
    row.onmouseleave = () => {
      if (!isSelected) row.style.background = "transparent";
      delBtn.style.opacity = "0";
    };
    delBtn.onclick = (e) => {
      e.stopPropagation();
      this._deleteCapture(c);
    };
    row.appendChild(delBtn);

    return row;
  }

  private async _deleteCapture(c: ManifestCapture): Promise<void> {
    if (!confirm(`Delete capture "${c.filename}"?\nThe file will be removed from storage.`)) return;
    try {
      await api.ic.deleteCapture(this.project.project_id, c.id);
      this.allCaptures = this.allCaptures.filter((x) => x.id !== c.id);
      if (this.selectedCapture?.id === c.id) this.selectedCapture = null;
      this.renderCaptureList();
    } catch (err) {
      alert(`Failed to delete capture: ${err}`);
    }
  }

  private _makeGenerateButton(c: ManifestCapture): HTMLElement | null {
    // Derive job type from media_type
    let jobType: "glb" | "floorplan" | "transcript" | null = null;
    if (c.media_type === "roomplan_usdz") jobType = "glb";
    else if (c.media_type === "roomplan_json") jobType = "floorplan";
    else if (c.media_type === "video") jobType = "transcript";
    if (!jobType || !c.uploaded) return null;

    const job = this.captureJobsBySourceId.get(c.id);
    if (job) {
      const inFlight = job.status === "pending" || job.status === "submitted" || job.status === "processing";
      if (inFlight) return _makeStatusBadge("pending");
      // Job done/failed/cancelled for glb/floorplan → derived capture will appear in list
      if (jobType !== "transcript") return null;
      // Transcript done but manifest not yet updated → keep spinner until text arrives
      if (job.status === "done" && !c.transcript_text) return _makeStatusBadge("pending");
      if (c.transcript_text) return null;
    }

    // No active job: check if result already exists
    if (jobType !== "transcript") {
      const hasDerived = this.allCaptures.some(
        (x) => x.derived_from_capture_id === c.id && x.media_type === (jobType === "glb" ? "glb" : "floorplan_svg"),
      );
      if (hasDerived) return null;
    } else {
      if (c.transcript_text) return null;
    }

    const label = jobType === "glb" ? "GLB" : jobType === "floorplan" ? "SVG" : "Transcribe";
    const btn = document.createElement("button");
    btn.textContent = label;
    btn.title = `Generate ${label}`;
    btn.style.cssText =
      "flex-shrink:0;padding:2px 7px;font-size:10px;font-weight:600;" +
      "border:1px solid var(--border);border-radius:3px;cursor:pointer;" +
      "color:var(--text-muted);background:var(--surface);line-height:1.6;" +
      "transition:background 80ms;";
    btn.onmouseenter = () => { btn.style.background = "var(--surface2)"; };
    btn.onmouseleave = () => { btn.style.background = "var(--surface)"; };
    btn.onclick = async (e) => {
      e.stopPropagation();
      btn.disabled = true;
      btn.textContent = "…";
      try {
        await api.ic.triggerCaptureGenerate(this.project.project_id, c.id, jobType!);
        // Optimistic placeholder — same for all job types including transcript
        this.captureJobsBySourceId.set(c.id, {
          capture_id: c.id,
          job_type: jobType!,
          job_id: null,
          status: "pending",
          output_capture_id: null,
          last_error: null,
          created_at: new Date().toISOString(),
        });
        this._scheduleJobPoll();
        this.renderCaptureList();
      } catch (err) {
        btn.disabled = false;
        btn.textContent = label;
        this.opts.toast?.(`Generate failed: ${(err as Error).message}`, "error");
      }
    };
    return btn;
  }

  /** Project-root scan outputs surfaced as virtual captures. */
  private _isOperatorVisible(c: ManifestCapture): boolean {
    if (c.media_type === "roomplan_dxf") return true;
    if (c.phase === "scan") return false;
    if (c.phase === "plan" && c.media_type !== "roomplan_plan_png") return false;
    return true;
  }

  private _buildGlbVirtualCaptures(files: IcScanFile[]): ManifestCapture[] {
    const fallbackInspectionId = this.manifest?.inspections[0]?.id ?? "";
    const fallbackDate = this.manifest?.inspections
      .map((i) => i.date)
      .filter((d): d is string => !!d)
      .sort()
      .pop() ?? new Date().toISOString().slice(0, 10);

    const out: ManifestCapture[] = [];
    for (const f of files) {
      const kind = this._scanFileKind(f);
      out.push({
        id: `glb:${f.name}`,
        inspection_id: fallbackInspectionId,
        room_id: kind.roomId,
        phase: "scan",
        intent: null,
        media_type: kind.mediaType,
        filename: f.name,
        drive_file_id: null,
        uploaded: true,
        captured_at: fallbackDate,
        meter_reading: null,
        equipment_qr: null,
        transcript_text: null,
        field_note: f.size != null ? _formatBytes(f.size) : null,
        parent_capture_id: null,
        root_capture_id: null,
        resolution_inspection_id: null,
        heading: null,
        pitch: null,
        roll: null,
        gps_lat: null,
        gps_lon: null,
        gps_accuracy_m: null,
      });
    }
    return out;
  }

  /**
   * Classify a scan-file by filename so the House composer's walls backfill
   * (which looks for media_type === "roomplan_model_glb") can find the
   * RoomPlan wall meshes that inspect_converter emits alongside textured
   * scans. The IC manifest does not surface these, and the backend
   * `_scan_file_role` returns them with role "glb" since they end in .glb
   * (not _model.glb), so the disambiguation has to happen here.
   *
   * Recognised shapes:
   *  - <prefix>_<room>_plan_NNNN_model_aligned.glb  → roomplan_model_glb
   *  - <prefix>_<room>_model.glb                    → roomplan_model_glb (legacy)
   *  - everything else → fall back to f.role
   */
  private _scanFileKind(f: IcScanFile): { mediaType: string; roomId: string | null } {
    const wallMatch =
      f.name.match(/^[0-9a-z]+_(.+)_plan_\d+_model_aligned\.glb$/i) ??
      f.name.match(/^[0-9a-z]+_(.+)_model\.glb$/i);
    if (wallMatch) {
      return {
        mediaType: "roomplan_model_glb",
        roomId: this._roomIdFromSlug(wallMatch[1]),
      };
    }
    const defaultMt = f.role === "usdz" ? "usdz" : "glb";
    return { mediaType: defaultMt, roomId: this._matchScanRoom(f.name) };
  }

  private _roomIdFromSlug(rawSlug: string): string | null {
    if (!this.manifest) return null;
    const slug = rawSlug.toLowerCase().replace(/_/g, " ").trim();
    for (const r of this.manifest.rooms) {
      if (r.name.toLowerCase().trim() === slug) return r.id;
    }
    return null;
  }

  /** Match scan filename like "<prefix>_kitchen_scan_0001.glb" to a room id. */
  private _matchScanRoom(filename: string): string | null {
    const m = filename.match(/^[0-9a-z]+_(.+)_scan_\d+\.glb$/i);
    if (!m) return null;
    return this._roomIdFromSlug(m[1]);
  }

  // ── Center pane ──────────────────────────────────────────────────────────

  private selectCapture(c: ManifestCapture): void {
    this.selectedCapture = c;

    const isUsdz =
      c.media_type === "usdz" ||
      c.media_type === "roomplan_usdz" ||
      (!c.id.startsWith("glb:") && c.filename?.toLowerCase().endsWith(".usdz"));
    const isGlb =
      c.media_type === "glb" ||
      c.filename?.toLowerCase().endsWith(".glb");

    // GLB files open the inline Three.js viewer.
    if (isGlb) {
      void this.mountGlbViewer(c);
      return;
    }
    // USDZ from regular captures (not virtual scan-file rows): Three.js has
    // very limited USDA support and fails on RoomPlan/complex USDZ. Show a
    // download card instead.
    if (isUsdz) {
      this._mountUsdzCard(c);
      return;
    }
    // Virtual USDZ scan-file rows (id starts with "glb:") still try Three.js.
    if (c.id.startsWith("glb:") && c.filename?.toLowerCase().endsWith(".usdz")) {
      void this.mountGlbViewer(c);
      return;
    }
    // Dispose 3D viewer if open so center is free
    if (this.viewer3DDispose) {
      this.viewer3DDispose();
      this.viewer3DDispose = null;
    }
    this.centerMode = "capture";
    this.renderCenter();
    // Refresh row highlights
    this.renderCaptureList();
  }

  private async mountGlbViewer(capture: ManifestCapture): Promise<void> {
    if (!this.opts.embed3DFromUrl) {
      this.opts.toast?.("3D viewer is not available in this build", "error");
      return;
    }
    if (this.viewer3DDispose || this.viewer3DMounting) return;

    this.viewer3DMounting = true;
    this.centerMode = "3d";
    this._disposeHiddenVideo();
    this._centerContent.innerHTML = "";
    this.renderCaptureList();

    const filename = capture.filename;
    const overlay = this._createGlbLoadingOverlay(filename);
    this._centerContent.appendChild(overlay);

    // Virtual scan-file captures (id starts with "glb:") are served from the
    // scan-files endpoint. Real manifest captures are served via presigned URL
    // with a blob-URL fallback.
    const isVirtual = capture.id.startsWith("glb:");
    let url: string;
    try {
      if (isVirtual) {
        url = await api.ic.scanFileBlobUrlWithProgress(
          this.project.project_id,
          filename,
          (received, total) => this._updateGlbProgress(overlay, received, total),
        );
      } else {
        // Presigned URLs are direct R2 links without CORS headers — fetch via
        // the backend proxy to get a blob: URL that Three.js can load.
        url = await api.ic.captureMediaBlobUrl(this.project.project_id, capture.id);
      }
    } catch (err) {
      this.viewer3DMounting = false;
      overlay.remove();
      this.opts.toast?.(
        `Failed to fetch 3D file: ${(err as Error).message}`,
        "error",
      );
      this.centerMode = "placeholder";
      this.renderCenter();
      return;
    }
    this._setGlbLoadingStage(overlay, "Opening 3D viewer…");
    try {
      this.viewer3DDispose = await this.opts.embed3DFromUrl(url, this.centerPane, {
        localProjectId: this.local3D?.local_project_id ?? undefined,
        icProjectId: this.project.project_id,
      });
    } catch (err) {
      this.viewer3DMounting = false;
      overlay.remove();
      this.opts.toast?.(
        `Could not open 3D viewer: ${(err as Error).message}`,
        "error",
      );
      this.centerMode = "placeholder";
      this.renderCenter();
      return;
    }
    overlay.remove();
    this.viewer3DMounting = false;
  }

  private _createGlbLoadingOverlay(filename: string): HTMLElement {
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#0d0f12;color:#e6e6e6;z-index:5;font:13px/1.4 system-ui,sans-serif;";
    const card = document.createElement("div");
    card.style.cssText =
      "display:flex;flex-direction:column;gap:10px;min-width:280px;max-width:80%;padding:18px 22px;background:#16191e;border:1px solid #2a2f37;border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,0.4);";
    const stage = document.createElement("div");
    stage.dataset.stage = "1";
    stage.style.cssText = "font-weight:600;font-size:14px;color:#fff;";
    stage.textContent = "Loading GLB…";
    const name = document.createElement("div");
    name.style.cssText =
      "font-size:12px;color:#9aa3ad;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
    name.textContent = filename;
    const barWrap = document.createElement("div");
    barWrap.style.cssText =
      "position:relative;height:6px;background:#262b33;border-radius:3px;overflow:hidden;margin-top:4px;";
    const bar = document.createElement("div");
    bar.dataset.bar = "1";
    bar.style.cssText =
      "height:100%;width:0%;background:#4a9eff;transition:width 120ms linear;";
    barWrap.appendChild(bar);
    const text = document.createElement("div");
    text.dataset.text = "1";
    text.style.cssText = "font-size:12px;color:#9aa3ad;";
    text.textContent = "Starting…";
    card.appendChild(stage);
    card.appendChild(name);
    card.appendChild(barWrap);
    card.appendChild(text);
    overlay.appendChild(card);
    return overlay;
  }

  private _updateGlbProgress(
    overlay: HTMLElement,
    received: number,
    total: number | null,
  ): void {
    const bar = overlay.querySelector<HTMLDivElement>("[data-bar]");
    const text = overlay.querySelector<HTMLDivElement>("[data-text]");
    const fmt = (n: number): string => {
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
      return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };
    if (total && total > 0) {
      const pct = Math.min(100, (received / total) * 100);
      if (bar) bar.style.width = `${pct.toFixed(1)}%`;
      if (text) text.textContent = `${fmt(received)} / ${fmt(total)} (${pct.toFixed(0)}%)`;
    } else {
      if (bar) bar.style.width = "100%";
      if (text) text.textContent = `${fmt(received)} downloaded`;
    }
  }

  private _setGlbLoadingStage(overlay: HTMLElement, msg: string): void {
    const stage = overlay.querySelector<HTMLDivElement>("[data-stage]");
    if (stage) stage.textContent = msg;
    const bar = overlay.querySelector<HTMLDivElement>("[data-bar]");
    if (bar) {
      bar.style.width = "100%";
      bar.style.background =
        "repeating-linear-gradient(45deg,#4a9eff,#4a9eff 8px,#3a7ecf 8px,#3a7ecf 16px)";
      bar.style.animation = "ic-glb-stripes 0.8s linear infinite";
    }
    if (!document.getElementById("ic-glb-stripes-kf")) {
      const style = document.createElement("style");
      style.id = "ic-glb-stripes-kf";
      style.textContent =
        "@keyframes ic-glb-stripes{from{background-position:0 0}to{background-position:32px 0}}";
      document.head.appendChild(style);
    }
  }

  private renderCenter(): void {
    // Guard: viewer DOM currently lives in centerPane — don't destroy it
    if (this.viewer3DDispose || this.viewer3DMounting) return;
    // Guard: house composer owns the center — don't clear it
    if (this.leftMode === "house") return;
    this._clearClientPreview();
    this._disposeHiddenVideo();
    this._capturePanoViewer?.dispose();
    this._capturePanoViewer = null;
    this._centerContent.innerHTML = "";

    if (this.centerMode === "capture" && this.selectedCapture) {
      this.renderCaptureMedia(this.selectedCapture);
    } else {
      this.renderCenterDefault();
    }
  }

  private renderCaptureMedia(c: ManifestCapture): void {
    const wrap = document.createElement("div");
    wrap.style.cssText =
      "width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#000;position:relative;overflow:hidden;";

    // For the transcript layout the bar must sit inside playerWrap (position:relative),
    // not wrap. This variable is reassigned in that branch.
    let barHost: HTMLElement = wrap;

    const isImage =
      c.media_type === "photo" ||
      c.media_type === "roomplan_plan_png";

    if (c.media_type === "panorama_frame" && c.uploaded) {
      const viewer = new CapturePanoViewer(wrap);
      this._capturePanoViewer = viewer;

      const applyUrl = (url: string) => viewer.loadUrl(url);
      void (async () => {
        try {
          applyUrl(await api.ic.capturePresignedUrl(this.project.project_id, c.id));
        } catch {
          try {
            applyUrl(await api.ic.captureMediaMediumBlobUrl(this.project.project_id, c.id));
          } catch {
            wrap.appendChild(this._mediaPlaceholder(c));
          }
        }
      })();
    } else if (isImage && c.uploaded) {
      const spinner = document.createElement("div");
      spinner.className = "ic-spinner";
      wrap.appendChild(spinner);

      const img = document.createElement("img");
      img.draggable = false;
      img.style.cssText =
        "position:absolute;left:0;top:0;transform-origin:0 0;opacity:0;transition:filter 280ms,opacity 150ms;";
      wrap.appendChild(img);

      // ── Zoom / pan state ─────────────────────────────────────────────────
      let scale = 1, sx = 0, sy = 0;

      const applyTransform = () => {
        img.style.transform = `translate(${sx}px,${sy}px) scale(${scale})`;
      };

      const fitImage = () => {
        const { width: cw, height: ch } = wrap.getBoundingClientRect();
        const nw = img.naturalWidth, nh = img.naturalHeight;
        if (!nw || !nh || !cw || !ch) return;
        img.style.width = `${nw}px`;
        img.style.height = `${nh}px`;
        scale = Math.min(cw / nw, ch / nh);
        sx = (cw - nw * scale) / 2;
        sy = (ch - nh * scale) / 2;
        applyTransform();
      };

      wrap.addEventListener("wheel", (e) => {
        e.preventDefault();
        const r = wrap.getBoundingClientRect();
        const cx = e.clientX - r.left, cy = e.clientY - r.top;
        const factor = Math.exp(-e.deltaY * 0.0015);
        const next = Math.max(0.05, Math.min(20, scale * factor));
        const k = next / scale;
        sx = cx - k * (cx - sx);
        sy = cy - k * (cy - sy);
        scale = next;
        applyTransform();
      }, { passive: false });

      let dragging = false, dsx = 0, dsy = 0, dmx = 0, dmy = 0;
      wrap.style.cursor = "grab";
      wrap.addEventListener("pointerdown", (e) => {
        if (e.button !== 0) return;
        dragging = true; dsx = sx; dsy = sy; dmx = e.clientX; dmy = e.clientY;
        wrap.style.cursor = "grabbing";
        wrap.style.userSelect = "none";
        wrap.setPointerCapture(e.pointerId);
      });
      wrap.addEventListener("pointermove", (e) => {
        if (!dragging) return;
        sx = dsx + (e.clientX - dmx); sy = dsy + (e.clientY - dmy);
        applyTransform();
      });
      const endDrag = (e: PointerEvent) => {
        if (!dragging) return;
        dragging = false;
        wrap.style.cursor = "grab";
        wrap.style.userSelect = "";
        try { wrap.releasePointerCapture(e.pointerId); } catch { /* noop */ }
      };
      wrap.addEventListener("pointerup", endDrag);
      wrap.addEventListener("pointercancel", endDrag);
      wrap.addEventListener("dblclick", fitImage);

      // ── Two-phase image loading ───────────────────────────────────────────
      // Phase 1 — show thumbnail immediately (likely already in memory cache).
      // It appears blurry so the user knows a sharper version is loading.
      let phaseReached = 0;
      const projectId = this.project.project_id;
      api.ic.captureMediaThumbBlobUrl(projectId, c.id).then((url) => {
        if (phaseReached < 1) {
          phaseReached = 1;
          img.src = url;
          img.style.opacity = "1";
          img.style.filter = "blur(8px)";
          spinner.remove();
          img.addEventListener("load", () => requestAnimationFrame(fitImage), { once: true });
        }
      }).catch(() => {});

      // Phase 2 — full-resolution. Prefer a presigned R2 URL so the browser
      // streams the original straight from object storage (zero proxy hops,
      // browser-cached by URL). Falls back to the disk-cached medium JPEG
      // when the storage backend doesn't support presigned URLs (e.g. Drive).
      const applyPhase2 = (url: string) => {
        if (phaseReached >= 2) return;
        phaseReached = 2;
        const preload = new Image();
        preload.onload = () => {
          img.src = url;
          img.style.opacity = "1";
          img.style.filter = "";
          spinner.remove();
          img.addEventListener("load", () => requestAnimationFrame(fitImage), { once: true });
        };
        preload.src = url;
      };
      void (async () => {
        try {
          applyPhase2(await api.ic.capturePresignedUrl(projectId, c.id));
        } catch {
          try {
            applyPhase2(await api.ic.captureMediaMediumBlobUrl(projectId, c.id));
          } catch {
            if (phaseReached === 0) {
              img.remove();
              spinner.remove();
              wrap.appendChild(this._mediaPlaceholder(c));
            }
          }
        }
      })();
    } else if (c.media_type === "roomplan_json" && c.uploaded) {
      const spinner = document.createElement("div");
      spinner.className = "ic-spinner";
      wrap.appendChild(spinner);
      void (async () => {
        try {
          const blobUrl = await api.ic.captureMediaBlobUrl(this.project.project_id, c.id);
          const res = await fetch(blobUrl);
          const json = await res.json() as IcRoomPlanJson;
          spinner.remove();
          renderRoomPlanJson2D(wrap, json);
        } catch {
          spinner.remove();
          wrap.appendChild(this._mediaPlaceholder(c));
        }
      })();
    } else if (c.media_type === "roomplan_dxf" && c.uploaded) {
      // CAD floor plan — render inline with dxf-kit's <dxf-viewer> web
      // component (toolbar, rulers/measure, layer + properties panels,
      // export). dxf-lit is dynamically imported so its three bundle loads
      // only when a DXF capture is actually opened.
      const spinner = document.createElement("div");
      spinner.className = "ic-spinner";
      wrap.appendChild(spinner);
      void (async () => {
        try {
          // Fetch DXF text and import dxf-lit concurrently.
          const [dxfText] = await Promise.all([
            api.ic.captureMediaText(this.project.project_id, c.id),
            import("dxf-lit"),  // registers <dxf-viewer> / <dxf-layer-panel> / <dxf-ruler>
          ]);
          spinner.remove();
          const viewer = document.createElement("dxf-viewer") as DxfViewerElement;
          viewer.style.cssText = "width:100%;height:100%;display:block;";
          viewer.fileName = c.filename;
          viewer.darkTheme = true;
          viewer.showLayerPanel = true;     // AIA layers: A-WALL / A-DOOR / A-GLAZ / …
          viewer.groupLayers = true;        // group by A-* prefix
          viewer.showPropertiesPanel = true;
          viewer.showRulers = true;
          viewer.showMeasureButton = true;
          viewer.showMeasureAreaButton = true;
          viewer.showExportButton = true;
          viewer.rulerUnits = "mm";          // drawing units are millimetres ($INSUNITS=4)
          wrap.appendChild(viewer);
          // Pass text directly — bypasses internal fetch and any Content-Type
          // ambiguity that can corrupt the string in the blob-URL path.
          await viewer.loadDXFFromText(dxfText);
        } catch {
          spinner.remove();
          wrap.appendChild(this._mediaPlaceholder(c));
        }
      })();
    } else if (c.media_type === "video" && c.uploaded) {
      const segments = c.transcript_segments;

      if (segments && segments.length > 0) {
        // ── Video + transcript layout ────────────────────────────────────
        wrap.style.cssText = "width:100%;height:100%;overflow:hidden;";
        const outer = document.createElement("div");
        outer.className = "vt-outer";

        const playerWrap = document.createElement("div");
        playerWrap.className = "vt-player";

        const v = document.createElement("video");
        v.className = "vt-video";
        v.controls = true;
        v.preload = "metadata";
        playerWrap.appendChild(v);
        outer.appendChild(playerWrap);
        barHost = playerWrap;

        // Segments panel
        const segPanel = document.createElement("div");
        segPanel.className = "vt-segments";

        // Language toggle — shown only when at least one segment carries a
        // translation. Defaults to English; original text always lives in
        // `seg.text`, the inspect_processor Whisper run produces `text_en`.
        const hasTranslation = segments.some((s) => !!s.text_en);
        let showEn = hasTranslation;  // default: English when available
        const textTargets: HTMLElement[] = [];
        const applyLang = (): void => {
          for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const target = textTargets[i];
            if (!target) continue;
            target.textContent = showEn && seg.text_en ? seg.text_en : seg.text;
          }
        };
        if (hasTranslation) {
          const langBar = document.createElement("div");
          langBar.className = "vt-lang-bar";
          const toggle = document.createElement("div");
          toggle.className = "vt-lang-toggle";
          const btnEn = document.createElement("button");
          btnEn.type = "button";
          btnEn.textContent = "EN";
          const btnOrig = document.createElement("button");
          btnOrig.type = "button";
          btnOrig.textContent = "Original";
          const sync = (): void => {
            btnEn.classList.toggle("active", showEn);
            btnOrig.classList.toggle("active", !showEn);
          };
          btnEn.addEventListener("click", () => { if (!showEn) { showEn = true; sync(); applyLang(); } });
          btnOrig.addEventListener("click", () => { if (showEn) { showEn = false; sync(); applyLang(); } });
          sync();
          toggle.appendChild(btnEn);
          toggle.appendChild(btnOrig);
          langBar.appendChild(toggle);
          segPanel.appendChild(langBar);
        }

        const segList = document.createElement("div");
        segList.className = "vt-seg-list";
        segPanel.appendChild(segList);
        outer.appendChild(segPanel);

        wrap.appendChild(outer);

        // Per-capture frame cache (persists across re-selections)
        if (!this._captureFrameCache.has(c.id)) {
          this._captureFrameCache.set(c.id, new Map());
        }
        const frameCache = this._captureFrameCache.get(c.id)!;

        // Render segment rows
        let activeSegIdx = -1;
        const items: HTMLElement[] = [];

        const selectSeg = (idx: number, seek: boolean): void => {
          if (activeSegIdx === idx && !seek) return;
          if (activeSegIdx >= 0) items[activeSegIdx]?.classList.remove("active");
          activeSegIdx = idx;
          items[idx]?.classList.add("active");
          items[idx]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          if (seek && v.src) v.currentTime = segments[idx].time_start;
        };

        v.addEventListener("timeupdate", () => {
          const t = v.currentTime;
          for (let i = 0; i < segments.length; i++) {
            if (t >= segments[i].time_start && t < segments[i].time_end) {
              if (i !== activeSegIdx) selectSeg(i, false);
              return;
            }
          }
        });

        for (let i = 0; i < segments.length; i++) {
          const seg = segments[i];
          const item = document.createElement("div");
          item.className = "vt-seg-item";

          const thumb = document.createElement("div");
          thumb.className = "vt-seg-thumb";
          const cached = frameCache.get(seg.time_start);
          if (cached) {
            const img = new Image();
            img.src = cached;
            thumb.appendChild(img);
          } else {
            thumb.innerHTML = '<div class="ic-thumb-shimmer"></div>';
          }

          const body = document.createElement("div");
          body.className = "vt-seg-body";
          const timeSpan = document.createElement("span");
          timeSpan.className = "vt-seg-time";
          timeSpan.textContent = `${_vtFmtTime(seg.time_start)} – ${_vtFmtTime(seg.time_end)}`;
          const textSpan = document.createElement("span");
          textSpan.className = "vt-seg-text";
          textSpan.textContent = showEn && seg.text_en ? seg.text_en : seg.text;
          textTargets.push(textSpan);
          body.appendChild(timeSpan);
          body.appendChild(textSpan);

          item.appendChild(thumb);
          item.appendChild(body);
          item.addEventListener("click", () => selectSeg(i, true));
          segList.appendChild(item);
          items.push(item);
        }

        // Load blob URL → set main video + kick off hidden-video frame capture.
        // Use the H.264 sidecar profile so Firefox/Chrome can decode HEVC
        // sources; IC falls back to the original when no sidecar exists.
        api.ic.captureMediaWebBlobUrl(this.project.project_id, c.id).then((blobUrl) => {
          v.src = blobUrl;

          // Hidden video for seeking & frame capture
          const hv = document.createElement("video");
          hv.preload = "auto";
          hv.style.cssText = "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;top:-9999px;";
          hv.src = blobUrl;
          document.body.appendChild(hv);
          this._hiddenCaptureVideo = hv;

          const captureQueue = segments
            .map((_, i) => i)
            .filter(i => !frameCache.has(segments[i].time_start));
          let busy = false;

          const processNext = (): void => {
            if (busy || captureQueue.length === 0) return;
            busy = true;
            const i = captureQueue.shift()!;
            const seg = segments[i];
            hv.currentTime = seg.time_start;
            hv.addEventListener("seeked", () => {
              const W = hv.videoWidth || 320;
              const H = hv.videoHeight || 180;
              const canvas = document.createElement("canvas");
              canvas.width = W;
              canvas.height = H;
              canvas.getContext("2d")!.drawImage(hv, 0, 0, W, H);
              const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
              frameCache.set(seg.time_start, dataUrl);
              const thumbEl = items[i]?.querySelector(".vt-seg-thumb") as HTMLElement | null;
              if (thumbEl) {
                thumbEl.innerHTML = "";
                const img = new Image();
                img.src = dataUrl;
                thumbEl.appendChild(img);
              }
              busy = false;
              processNext();
            }, { once: true });
          };

          if (hv.readyState >= 1) {
            processNext();
          } else {
            hv.addEventListener("loadedmetadata", processNext, { once: true });
          }
        }).catch(() => {
          outer.remove();
          wrap.style.cssText = "width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#000;position:relative;overflow:hidden;";
          wrap.appendChild(this._mediaPlaceholder(c));
        });

      } else {
        // ── Plain video + optional transcript_text fallback ──────────────
        if (c.transcript_text) {
          wrap.style.flexDirection = "column";
          wrap.style.alignItems = "stretch";
        }
        const v = document.createElement("video");
        v.controls = true;
        v.style.cssText = c.transcript_text
          ? "width:100%;flex:1 1 0;min-height:0;object-fit:contain;"
          : "max-width:100%;max-height:100%;";
        wrap.appendChild(v);
        api.ic
          .captureMediaWebBlobUrl(this.project.project_id, c.id)
          .then((blobUrl) => { v.src = blobUrl; })
          .catch(() => {
            v.remove();
            wrap.appendChild(this._mediaPlaceholder(c));
          });

        if (c.transcript_text) {
          const tx = document.createElement("div");
          tx.style.cssText =
            "flex:0 0 auto;max-height:35%;overflow-y:auto;padding:10px 14px;" +
            "color:#ccc;font-size:13px;line-height:1.55;background:rgba(0,0,0,.75);";
          tx.textContent = c.transcript_text;
          wrap.appendChild(tx);
        }
      }
    } else {
      wrap.appendChild(this._mediaPlaceholder(c));
    }

    // Top metadata bar (phase + room overlay)
    const bar = document.createElement("div");
    bar.style.cssText = `
      position:absolute;top:0;left:0;right:0;
      padding:10px 14px;
      background:linear-gradient(to bottom,rgba(0,0,0,.65),transparent);
      display:flex;align-items:center;gap:10px;
      pointer-events:none;
    `;
    const phaseChip = document.createElement("span");
    phaseChip.textContent = `${c.phase}${c.intent ? " · " + c.intent : ""}`;
    phaseChip.style.cssText = `
      font-size:12px;font-weight:600;color:#fff;
      padding:2px 10px;border-radius:99px;
      background:${PHASE_COLORS[c.phase] || PHASE_COLORS.other}cc;
    `;
    bar.appendChild(phaseChip);
    const room = this.roomById.get(c.room_id || "");
    if (room) {
      const roomSpan = document.createElement("span");
      roomSpan.style.cssText = "font-size:12px;color:rgba(255,255,255,.65);";
      roomSpan.textContent = room.name;
      bar.appendChild(roomSpan);
    }
    barHost.appendChild(bar);

    this._centerContent.appendChild(wrap);
  }

  private _mountUsdzCard(c: ManifestCapture): void {
    if (this.viewer3DDispose) {
      this.viewer3DDispose();
      this.viewer3DDispose = null;
    }
    this.centerMode = "capture";
    this._disposeHiddenVideo();
    this._centerContent.innerHTML = "";
    this.renderCaptureList();

    const wrap = document.createElement("div");
    wrap.style.cssText =
      "width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#000;";

    const card = document.createElement("div");
    card.style.cssText = `
      display:flex;flex-direction:column;align-items:center;gap:16px;
      padding:32px 40px;background:#16191e;border:1px solid #2a2f37;
      border-radius:10px;color:#e6e6e6;text-align:center;max-width:360px;
    `;

    const icon = document.createElement("div");
    icon.style.cssText = "font-size:56px;line-height:1;";
    icon.textContent = "⬡";

    const title = document.createElement("div");
    title.style.cssText = "font-size:15px;font-weight:600;color:#fff;";
    title.textContent = c.filename || "USDZ model";

    const note = document.createElement("div");
    note.style.cssText = "font-size:13px;color:#888;line-height:1.5;max-width:280px;";
    note.textContent =
      "USDZ files can be viewed in AR on iPhone/iPad (Safari) or in Preview on Mac.";

    const btn = document.createElement("a");
    btn.style.cssText = `
      margin-top:4px;padding:9px 22px;border-radius:6px;font-size:13px;
      font-weight:600;cursor:pointer;text-decoration:none;color:#fff;
      background:var(--accent,#0a84ff);transition:opacity 120ms;
    `;
    btn.textContent = "Download USDZ";
    btn.onmouseenter = () => { btn.style.opacity = "0.82"; };
    btn.onmouseleave = () => { btn.style.opacity = "1"; };

    // Use presigned URL for the download link so it opens directly.
    api.ic.capturePresignedUrl(this.project.project_id, c.id)
      .then((url) => {
        btn.href = url;
        btn.download = c.filename || "model.usdz";
      })
      .catch(() => {
        btn.href = api.ic.captureMediaUrl(this.project.project_id, c.id);
        btn.download = c.filename || "model.usdz";
      });

    card.appendChild(icon);
    card.appendChild(title);
    card.appendChild(note);
    card.appendChild(btn);
    wrap.appendChild(card);
    this._centerContent.appendChild(wrap);
  }

  private _disposeHiddenVideo(): void {
    if (this._hiddenCaptureVideo) {
      this._hiddenCaptureVideo.pause();
      this._hiddenCaptureVideo.src = "";
      this._hiddenCaptureVideo.remove();
      this._hiddenCaptureVideo = null;
    }
  }

  private _mediaPlaceholder(c: ManifestCapture): HTMLElement {
    const ph = document.createElement("div");
    ph.style.cssText =
      "display:flex;flex-direction:column;align-items:center;gap:10px;color:#555;";
    const icon = document.createElement("div");
    icon.style.fontSize = "64px";
    icon.textContent = MEDIA_ICON[c.media_type] || "?";
    const label = document.createElement("div");
    label.style.cssText = "font-family:monospace;font-size:13px;";
    label.textContent = c.uploaded ? c.media_type : "not uploaded";
    ph.appendChild(icon);
    ph.appendChild(label);
    return ph;
  }

  private _showClientPreview(url: string): void {
    if (this.viewer3DDispose) {
      this.viewer3DDispose();
      this.viewer3DDispose = null;
    }
    this._clearClientPreview();
    this._disposeHiddenVideo();
    this._capturePanoViewer?.dispose();
    this._capturePanoViewer = null;
    this._centerContent.innerHTML = "";
    this.centerMode = "client-preview";

    const iframe = document.createElement("iframe");
    iframe.src = url;
    iframe.style.cssText = "position:absolute;inset:0;width:100%;height:100%;border:none;";
    this.centerPane.appendChild(iframe);
    this._clientPreviewIframe = iframe;

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "✕ Exit preview";
    closeBtn.className = "btn";
    closeBtn.style.cssText = "position:absolute;top:8px;left:8px;z-index:11;font-size:12px;padding:4px 10px;";
    closeBtn.onclick = () => {
      this._clearClientPreview();
      this.centerMode = "placeholder";
      this.renderCenterDefault();
    };
    this.centerPane.appendChild(closeBtn);
    this._clientPreviewCloseBtn = closeBtn;
  }

  private _clearClientPreview(): void {
    this._clientPreviewIframe?.remove();
    this._clientPreviewIframe = null;
    this._clientPreviewCloseBtn?.remove();
    this._clientPreviewCloseBtn = null;
  }

  private renderCenterDefault(): void {
    const wrap = document.createElement("div");
    wrap.style.cssText =
      "flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:32px;text-align:center;color:var(--text-muted);";

    const status = this.local3D?.status ?? "none";

    const icon = document.createElement("div");
    const titleEl = document.createElement("div");
    titleEl.style.cssText = "font-size:15px;color:var(--text);font-weight:600;";
    const detail = document.createElement("div");
    detail.style.cssText =
      "max-width:340px;font-size:13px;color:var(--text-muted);line-height:1.55;";

    if (status === "ready" && this.local3D) {
      icon.style.cssText = "font-size:36px;opacity:.5;";
      icon.textContent = "⬡";
      titleEl.textContent = "3D assets ready";
      detail.textContent =
        "Select a capture from the left panel to preview it here, or open the 3D viewer.";
      wrap.appendChild(icon);
      wrap.appendChild(titleEl);
      wrap.appendChild(detail);
      wrap.appendChild(this._assetChips(this.local3D));
      wrap.appendChild(this._openButton());
    } else if (status === "processing" || status === "pending") {
      icon.style.cssText = "font-size:36px;opacity:.5;";
      icon.textContent = "⏳";
      titleEl.textContent = "Processing 3D assets…";
      detail.textContent =
        "The local pipeline is ingesting mesh / RoomPlan / panoramas. Refresh in a moment.";
      wrap.appendChild(icon);
      wrap.appendChild(titleEl);
      wrap.appendChild(detail);
    } else if (status === "error") {
      icon.style.cssText = "font-size:36px;color:var(--red);";
      icon.textContent = "✕";
      titleEl.textContent = "3D ingest failed";
      detail.textContent = this.local3D?.error_msg || "Unknown error during pipeline run.";
      wrap.appendChild(icon);
      wrap.appendChild(titleEl);
      wrap.appendChild(detail);
    } else {
      icon.style.cssText = "font-size:48px;opacity:.2;";
      icon.textContent = "⌬";
      detail.textContent = "Select a capture from the left panel to preview it here.";
      wrap.appendChild(icon);
      wrap.appendChild(detail);
    }

    this._centerContent.appendChild(wrap);
  }

  private _assetChips(s: IcLocal3DStatus): HTMLElement {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;flex-wrap:wrap;gap:6px;justify-content:center;";
    const items: Array<[string, boolean]> = [
      [s.mesh_role === "glb" ? "GLB mesh" : s.mesh_role === "ply" ? "PLY mesh" : s.mesh_role === "usdz" ? "USDZ mesh" : "mesh", s.has_glb || s.has_ply || s.mesh_role === "usdz"],
      ["Floor plan", s.has_floorplan],
      ["RoomPlan", s.has_roomplan_model || s.has_rooms_data],
      ["Pano360", s.has_pano360],
      ["Streetview", s.has_streetview],
    ];
    for (const [label, ok] of items) {
      if (!ok) continue;
      const chip = document.createElement("span");
      chip.textContent = `✓ ${label}`;
      chip.style.cssText =
        "font-size:11px;padding:2px 10px;border-radius:99px;background:rgba(74,255,106,.15);color:#4aff6a;font-weight:600;";
      row.appendChild(chip);
    }
    return row;
  }

  private _openButton(): HTMLElement {
    const btn = document.createElement("button");
    btn.className = "btn primary";
    btn.textContent = "▶ Open 3D viewer";
    btn.style.cssText += "margin-top:6px;padding:8px 18px;";
    btn.onclick = () => this.mountInlineViewer(btn);
    return btn;
  }

  private async mountInlineViewer(btn: HTMLButtonElement): Promise<void> {
    const localProjectId = this.local3D?.local_project_id;
    if (!localProjectId || !this.opts.embed3D) return;
    if (this.viewer3DDispose || this.viewer3DMounting) return;

    this.viewer3DMounting = true;
    btn.disabled = true;
    btn.textContent = "⏳ Loading 3D…";

    this._disposeHiddenVideo();
    this._centerContent.innerHTML = "";
    this.centerMode = "3d";
    this.selectedCapture = null;

    try {
      this.viewer3DDispose = await this.opts.embed3D(localProjectId, this.centerPane);
    } catch (err) {
      this.viewer3DMounting = false;
      this.opts.toast?.(
        `Could not open 3D viewer: ${(err as Error).message}`,
        "error",
      );
      this.centerMode = "placeholder";
      this.renderCenter();
      return;
    }
    this.viewer3DMounting = false;
  }

  // ── Right pane: tab bar ──────────────────────────────────────────────────

  private makeTabBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.style.cssText =
      "display:flex;align-items:stretch;border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;";

    const tabs: Array<{ key: TabKey; label: string }> = [
      { key: "overview", label: "Overview" },
      { key: "estimate", label: "Estimate" },
      { key: "compliance", label: "Compliance" },
    ];

    for (const t of tabs) {
      const btn = document.createElement("button");
      btn.textContent = t.label;
      btn.style.cssText = `
        padding:12px 18px;background:none;border:none;
        border-bottom:2px solid transparent;
        color:var(--text-muted);font-size:13px;font-weight:600;
        text-transform:uppercase;letter-spacing:.3px;
        cursor:pointer;transition:color 120ms,border-color 120ms;
      `;
      btn.onmouseenter = () => {
        if (this.activeTab !== t.key) btn.style.color = "var(--text)";
      };
      btn.onmouseleave = () => {
        if (this.activeTab !== t.key) btn.style.color = "var(--text-muted)";
      };
      btn.onclick = () => this.setTab(t.key);
      this.tabButtons.set(t.key, btn);
      bar.appendChild(btn);
    }

    const actions = document.createElement("div");
    actions.style.cssText =
      "margin-left:auto;display:flex;align-items:center;gap:2px;padding-right:6px;";

    const refresh = document.createElement("button");
    refresh.title = "Refresh manifest";
    refresh.textContent = "↻";
    refresh.style.cssText = this._iconBtnCss();
    refresh.onmouseenter = () => (refresh.style.color = "var(--text)");
    refresh.onmouseleave = () => (refresh.style.color = "var(--text-muted)");
    refresh.onclick = () => this.refresh();
    actions.appendChild(refresh);

    const collapseBtn = document.createElement("button");
    collapseBtn.title = "Hide manifest panel";
    collapseBtn.textContent = "⏵";
    collapseBtn.style.cssText = this._iconBtnCss();
    collapseBtn.onmouseenter = () => (collapseBtn.style.color = "var(--text)");
    collapseBtn.onmouseleave = () => (collapseBtn.style.color = "var(--text-muted)");
    collapseBtn.onclick = () => this.toggleRightPane();
    actions.appendChild(collapseBtn);

    bar.appendChild(actions);
    this._applyActiveStyle();
    return bar;
  }

  private _iconBtnCss(): string {
    return "background:none;border:none;cursor:pointer;color:var(--text-muted);font-size:16px;line-height:1;padding:8px 10px;border-radius:4px;transition:color 120ms,background 120ms;";
  }

  private _applyActiveStyle(): void {
    for (const [key, btn] of this.tabButtons) {
      const active = key === this.activeTab;
      btn.style.color = active ? "var(--accent)" : "var(--text-muted)";
      btn.style.borderBottomColor = active ? "var(--accent)" : "transparent";
    }
  }

  private setTab(tab: TabKey): void {
    this.activeTab = tab;
    this._applyActiveStyle();
    this.renderActiveTab();
  }

  private renderActiveTab(): void {
    this._resetRightContent();
    switch (this.activeTab) {
      case "overview":
        new OverviewTab({
          container: this.rightContent,
          project: this.project,
          manifest: this.manifest,
          manifestVersion: this.manifestVersion,
          manifestUpdatedAt: this.manifestUpdatedAt,
        });
        break;
      case "estimate":
        new EstimateTab({
          container: this.rightContent,
          manifest: this.manifest,
          project: this.project,
          onUpdate: () => this._refreshManifestForTab(),
        });
        break;
      case "compliance":
        new ComplianceTab({
          container: this.rightContent,
          project: this.project,
          manifest: this.manifest,
        });
        break;
    }
  }

  private _resetRightContent(): void {
    this.rightContent.innerHTML = "";
    this.rightContent.removeAttribute("style");
  }

  // ── Data loading ─────────────────────────────────────────────────────────

  private _renderLoading(): void {
    this._resetRightContent();
    const wrap = document.createElement("div");
    wrap.style.cssText =
      "padding:32px;color:var(--text-muted);text-align:center;font-size:13px;";
    wrap.textContent = "Loading manifest…";
    this.rightContent.appendChild(wrap);
  }

  private _scheduleJobPoll(): void {
    if (this._jobPollTimer) clearTimeout(this._jobPollTimer);
    this._jobPollTimer = null;

    const needsPoll = [...this.captureJobsBySourceId.values()].some((j) => {
      if (j.status === "pending" || j.status === "submitted" || j.status === "processing") return true;
      if (j.job_type === "transcript" && j.status === "done") {
        const c = this.allCaptures.find((x) => x.id === j.capture_id);
        return !c?.transcript_text;
      }
      return false;
    });

    if (needsPoll) {
      this._jobPollTimer = setTimeout(() => { this._jobPollTimer = null; this._pollJobs(); }, 3000);
    }
  }

  private async _pollJobs(): Promise<void> {
    try {
      const pid = this.project.project_id;
      const res = await api.ic.getCaptureJobs(pid);

      this.captureJobsBySourceId.clear();
      for (const j of res.jobs) this.captureJobsBySourceId.set(j.capture_id, j);

      // If a transcript job just finished, fetch the manifest to get transcript_text.
      const needsManifest = res.jobs.some(
        (j) => j.job_type === "transcript" && j.status === "done" &&
          !this.allCaptures.find((c) => c.id === j.capture_id)?.transcript_text,
      );
      if (needsManifest) {
        const mr = await api.ic.getManifest(pid);
        this.manifest = mr.manifest ?? null;
        this.manifestVersion = mr.manifest_version;
        this.manifestUpdatedAt = mr.updated_at;
        this.allCaptures = this.manifest
          ? this.manifest.inspections.flatMap((ins) => ins.captures)
          : [];
        if (this.selectedCapture) {
          const fresh = this.allCaptures.find((c) => c.id === this.selectedCapture!.id);
          if (fresh) {
            this.selectedCapture = fresh;
            if (fresh.transcript_text) this.renderActiveTab();
          }
        }
      }

      this.renderCaptureList();
      this._scheduleJobPoll();
    } catch {
      // Silently ignore transient poll errors; next scheduled refresh will retry.
    }
  }

  async refresh(): Promise<void> {
    this._renderLoading();
    try {
      // Fetch project metadata, manifest, 3D status, and capture jobs in parallel.
      const pid = this.project.project_id;
      const [projRes, manifestRes, local3DRes, captureJobsRes] = await Promise.allSettled([
        api.ic.getProject(pid),
        api.ic.getManifest(pid),
        api.ic.getLocal3D(pid),
        api.ic.getCaptureJobs(pid),
      ]);

      if (projRes.status === "fulfilled") {
        this.project = projRes.value.project;
      }

      if (manifestRes.status === "fulfilled") {
        this.manifest = manifestRes.value.manifest ?? null;
        this.manifestVersion = manifestRes.value.manifest_version;
        this.manifestUpdatedAt = manifestRes.value.updated_at;
      } else {
        const msg = (manifestRes.reason as Error).message || "";
        if (!msg.includes("No manifest") && !/404/.test(msg)) {
          this.opts.toast?.(`Manifest fetch failed: ${msg}`, "error");
        }
        this.manifest = null;
        this.manifestVersion = 0;
        this.manifestUpdatedAt = null;
      }

      if (local3DRes.status === "fulfilled") {
        this.local3D = local3DRes.value;
      } else {
        this.local3D = null;
      }

      // Pre-warm thumbnail cache for photo captures in the background.
      if (this.manifest) {
        const photoIds = this.manifest.inspections
          .flatMap((ins) => ins.captures)
          .filter((c) => c.media_type === "photo" && c.uploaded)
          .map((c) => c.id);
        api.ic.warmThumbs(pid, photoIds).catch(() => {});
      }

      // Index capture jobs by source capture_id
      this.captureJobsBySourceId.clear();
      if (captureJobsRes.status === "fulfilled") {
        for (const j of captureJobsRes.value.jobs) {
          this.captureJobsBySourceId.set(j.capture_id, j);
        }
      }

      // Flatten captures + index rooms + index inspections
      this.allCaptures = this.manifest
        ? this.manifest.inspections.flatMap((ins) => ins.captures)
        : [];
      this.roomById.clear();
      if (this.manifest) {
        for (const r of this.manifest.rooms) this.roomById.set(r.id, r);
      }
      // Project-root scan outputs (e.g. per-room GLB) live alongside the
      // manifest, not inside it. Surface them as virtual capture rows so
      // they're discoverable from the same sidebar.
      const glbFiles = (this.local3D?.scan_files ?? []).filter(
        (f) => f.role === "glb" || f.role === "usdz",
      );
      if (glbFiles.length) {
        this.allCaptures.push(...this._buildGlbVirtualCaptures(glbFiles));
      }
      this.captureInspections = this.manifest?.inspections ?? [];
      this.captureInspectionById.clear();
      for (const ins of this.captureInspections) this.captureInspectionById.set(ins.id, ins);
      const seenRooms = new Set<string>();
      this.captureRoomPairs = [];
      for (const c of this.allCaptures) {
        if (!c.room_id || seenRooms.has(c.room_id)) continue;
        seenRooms.add(c.room_id);
        this.captureRoomPairs.push([c.room_id, this.roomById.get(c.room_id)?.name ?? `room ${c.room_id.slice(0, 6)}`]);
      }

      // Update selectedCapture to the fresh object so the viewer reflects updated fields.
      if (this.selectedCapture) {
        const fresh = this.allCaptures.find((c) => c.id === this.selectedCapture!.id);
        if (fresh) this.selectedCapture = fresh;
      }

      // Schedule next poll if any job is still in flight or a transcript job finished
      // but its text hasn't appeared in the manifest yet.
      this._scheduleJobPoll();

      this.renderCapturesSidebar();
      this.renderCenter();
      this.renderActiveTab();
    } catch (err) {
      const msg = (err as Error).message || "Failed to load project";
      this._resetRightContent();
      const errEl = document.createElement("div");
      errEl.style.cssText =
        "padding:32px;color:var(--red);text-align:center;font-size:13px;";
      errEl.textContent = `Error: ${msg}`;
      this.rightContent.appendChild(errEl);
    }
  }

  private async _refreshManifestForTab(): Promise<void> {
    try {
      const pid = this.project.project_id;
      const manifestRes = await api.ic.getManifest(pid);
      this.manifest = manifestRes.manifest ?? null;
      this.manifestVersion = manifestRes.manifest_version;
      this.manifestUpdatedAt = manifestRes.updated_at;
      this.renderActiveTab();
    } catch {
      // Silently ignore; the tab will retain its current state.
    }
  }
}

function _formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function _vtFmtTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function _formatRelative(iso: string): string {
  const diffSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function _makeStatusBadge(jobStatus: "pending" | "submitted"): HTMLElement {
  const badge = document.createElement("span");
  badge.title = jobStatus === "pending" ? "Queued…" : "Processing…";
  badge.style.cssText =
    "flex-shrink:0;width:16px;height:16px;border-radius:50%;" +
    "border:2px solid var(--accent);border-top-color:transparent;" +
    "animation:ic-spin 0.8s linear infinite;display:inline-block;";
  // Ensure the keyframe is injected once
  if (!document.getElementById("ic-spin-style")) {
    const s = document.createElement("style");
    s.id = "ic-spin-style";
    s.textContent = "@keyframes ic-spin{to{transform:rotate(360deg)}}";
    document.head.appendChild(s);
  }
  return badge;
}
